import React, { useEffect, useMemo, useRef, useState } from "react";
import TabPage from "../../components/dynamic/Tab";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Clapperboard,
  Copy,
  Download,
  Eye,
  Grid2X2,
  ImageIcon,
  ImagePlus,
  KeyRound,
  Loader2,
  Package,
  PackagePlus,
  Palette,
  PenSquare,
  PlugZap,
  Plus,
  RefreshCcw,
  Save,
  Share2,
  Sparkles,
  Trash2,
  Type,
  Video,
} from "lucide-react";
import { SocialModuleProvider, useSocialModule } from "./SocialModuleContext";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";
import {
  buildProviderOptions,
  getDefaultModelForCapability,
} from "./socialShared";

/* ─────────────────── Shared primitives ─────────────────── */
const inputCls =
  "w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition-all placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-300 focus:bg-white focus:ring-4 focus:ring-blue-100";

function Field({ label, children }) {
  return (
    <label className="block text-xs font-semibold text-gray-700 tracking-tight">
      {label}
      <div className="mt-2 font-normal text-sm">{children}</div>
    </label>
  );
}

function SectionCard({ children, className = "" }) {
  return (
    <div className={`rounded-3xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>
      {children}
    </div>
  );
}

const IMAGE_CONTENT_SIZE_OPTIONS = [
  {
    value: "post",
    label: "Post (1:1)",
    ratio: "1:1",
    width: 1080,
    height: 1080,
  },
  {
    value: "story",
    label: "Story (9:16)",
    ratio: "9:16",
    width: 1080,
    height: 1920,
  },
  {
    value: "reel",
    label: "Reel (9:16)",
    ratio: "9:16",
    width: 1080,
    height: 1920,
  },
];

const IMAGE_QUALITY_OPTIONS = [
  {
    value: "standard",
    label: "Hızlı",
    icon: "⚡",
    hint: "Daha hızlı deneme üretimleri için.",
  },
  {
    value: "hq",
    label: "Yüksek",
    icon: "✨",
    hint: "Günlük sosyal medya üretimi için dengeli seçim.",
  },
  {
    value: "pro",
    label: "Pro",
    icon: "💎",
    hint: "Daha rafine, kampanya kalitesine yakın üretim için.",
  },
];

const getImageContentSize = (value) =>
  IMAGE_CONTENT_SIZE_OPTIONS.find((item) => item.value === value) ||
  IMAGE_CONTENT_SIZE_OPTIONS[0];

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(String(event.target?.result || ""));
    reader.onerror = () => reject(new Error("Dosya okunamadı."));
    reader.readAsDataURL(file);
  });

const loadCanvasImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Görsel yüklenemedi."));
    image.src = src;
  });

const drawImageCover = (ctx, image, width, height) => {
  const scale = Math.max(width / image.width, height / image.height);
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const offsetX = (width - drawWidth) / 2;
  const offsetY = (height - drawHeight) / 2;
  ctx.drawImage(image, offsetX, offsetY, drawWidth, drawHeight);
};

const formatSocialDate = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
};

const downloadSocialAsset = (item) => {
  if (!item?.url) return;
  const extension = item.type === "video" ? "mp4" : "png";
  const link = document.createElement("a");
  link.href = item.url;
  link.download = `social-${item.type || "icerik"}-${Date.now()}.${extension}`;
  link.target = "_blank";
  link.rel = "noreferrer";
  link.click();
};

function buildCampaignPosterPromptLines({ prompt, quality, sceneLocked }) {
  const promptText = String(prompt || "").trim();
  const wantsBadgeStyle = /rozet|badge|sticker|çıkartma|mühür|damga|seal/i.test(promptText);
  const wantsPlainPhoto = /düz fotoğraf|ham fotoğraf|raw photo|belgesel/i.test(promptText);

  return [
    wantsPlainPhoto
      ? null
      : "Düz bir fotoğraf değil, yayınlanabilir kampanya posteri estetiği üret.",
    sceneLocked
      ? "Mekânı değiştirmeden tasarım hissini tipografi, negatif alan, derinlik ve kurumsal yüzey detaylarıyla kur."
      : "Ham kamera karesi gibi durma; fotoğraf ve tasarım katmanı birlikte çalışan reklam kompozisyonu kur.",
    "Ana ürünü veya hizmeti net kahraman yap; arka plan destekleyici ve kontrollü kalsın.",
    quality === "pro"
      ? "Premium editoryal reklam hissi hedefle; güçlü tipografik hiyerarşi, temiz negatif alan ve rafine yerleşim kullan."
      : "Net tipografik hiyerarşi ve temiz yerleşim kullan.",
    "Metin kullanılacaksa tek güçlü başlık ve kısa destek satırıyla düzenli bloklar kur; metni rastgele boşluklara saçma şekilde serpiştirme.",
    sceneLocked
      ? "Metin ve görsel vurgu alanlarını sahnedeki doğal boşluklarda çöz; ürünün üstünü, masa yüzeyini ve ana objeyi kapatma."
      : "Kompozisyonda nefes alan negatif boşluk bırak; ürünün ve mesajın birbiriyle çarpışmasına izin verme.",
    "Kurumsal renklerle ince çerçeve, üst-alt şerit veya hafif yüzey detayları kullanabilirsin; tasarım hissini bunlarla kur, dev sticker ile değil.",
    wantsBadgeStyle
      ? null
      : "Dev dairesel indirim rozeti, sticker, mühür, market etiketi veya köşeye yapıştırılmış kampanya badge'i üretme.",
    wantsBadgeStyle
      ? null
      : "Logo ile kampanya mesajını aynı rozet, tabela veya etiket içinde birleştirme.",
    "Ürünü ortada bırakıp köşeye tek bir kampanya rozeti eklenmiş sıradan katalog estetiğine düşme.",
  ].filter(Boolean);
}

function buildNativeSocialImagePrompt({
  mode,
  prompt,
  contentSize,
  quality,
  logoHandling,
  brand,
  sceneLocked = false,
  personReferenceMode = false,
  selectedProducts = [],
}) {
  const sizeMeta = getImageContentSize(contentSize);
  const campaignPosterLines = buildCampaignPosterPromptLines({
    prompt,
    quality,
    sceneLocked,
  });
  const visualReferenceProducts = selectedProducts.filter((item) => item.mediaId || item.url);
  const visualReferenceSummary = visualReferenceProducts
    .map((item) => item.name)
    .filter(Boolean)
    .join(", ");
  const selectedProductSummary = selectedProducts
    .map((item) => {
      const parts = [item.name];
      if (item.description) {
        parts.push(item.description);
      }
      if (item.price) {
        parts.push(`özellik/fiyat: ${item.price}`);
      }
      return parts.filter(Boolean).join(" | ");
    })
    .join(" || ");
  const qualityLine =
    quality === "pro"
      ? "Premium, kampanya kalitesinde, rafine art direction hissi taşısın."
      : quality === "standard"
        ? "Temiz, hızlı ve net bir sosyal medya kreatifi olsun."
        : "Yüksek kaliteli, dengeli ışıklandırılmış ve profesyonel bir sosyal medya kreatifi olsun.";

  const lines = [
    mode === "edit"
      ? `Verilen kaynak görseli ana temel kabul et ve bunu tasarlanmış kampanya posteri estetiğinde profesyonel bir ${sizeMeta.label} sosyal medya görseline dönüştür.`
      : `Tasarlanmış kampanya posteri estetiğinde profesyonel bir ${sizeMeta.label} sosyal medya görseli üret.`,
    `Tuval ölçüsü: ${sizeMeta.width}x${sizeMeta.height}. Oran: ${sizeMeta.ratio}.`,
    brand?.name ? `Marka adı: ${brand.name}.` : null,
    brand?.sector ? `Marka sektörü: ${brand.sector}.` : null,
    brand?.description ? `Marka açıklaması: ${brand.description}.` : null,
    brand?.slogan ? `Marka sloganı: ${brand.slogan}.` : null,
    brand?.colors
      ? `Kurumsal renkler: ana=${brand.colors.primary}, ikincil=${brand.colors.secondary}, vurgu=${brand.colors.accent}.`
      : null,
    brand?.colors
      ? "Kurumsal renkleri zorunlu kimlik sistemi kabul et; ana rengi baskın yüzeylerde, ikincili denge alanlarında, vurgu rengini odak noktalarında etkili biçimde kullan."
      : null,
    brand?.personality ? `Marka tonu: ${brand.personality}.` : null,
    qualityLine,
    ...campaignPosterLines,
    selectedProducts.length
      ? `Seçilen ürün/hizmetler: ${selectedProductSummary}.`
      : null,
    selectedProducts.length === 1
      ? "Kompozisyonu seçilen ürün veya hizmet etrafında net biçimde kur."
      : null,
    selectedProducts.length > 1
      ? "Kompozisyonu yalnız seçilen ürün ve hizmetler etrafında kur; alakasız teklif, ürün veya sahne ekleme."
      : null,
    visualReferenceProducts.length
      ? `Seçilen ürün/hizmet görselleri ayrıca referans olarak verilecek: ${visualReferenceSummary}. Bu görselleri birebir ürün/hizmet kimliği kabul et; değiştirme, yeniden yorumlama veya başka ürün üretme.`
      : null,
    personReferenceMode
      ? "Yüklenen kişi görseli ayrıca referans olarak verilecek. Bu kişiyi reklam görselindeki ana model/insan referansı kabul et; yüz formunu, saç stilini, ten tonunu, yaş algısını, kıyafet ana hatlarını ve ayırt edici görünümü tutarlı koru."
      : null,
    personReferenceMode
      ? "Referans kişiyi doğal ışık, doğru anatomi ve temiz yüz detaylarıyla sahneye yerleştir; rastgele başka kişi, bozuk yüz, maske hissi veya karikatürize görünüm üretme."
      : null,
    sceneLocked
      ? "Seçilen kaynak görseldeki mekânı, arka planı, kadrajı, masa-sandalye yerleşimini, tezgâhı, duvarı ve ışığı koru; yeni bir mekân üretme."
      : null,
    selectedProducts.length
      ? "Seçilen ürün ve hizmetleri konu olarak kullan; sahne içine aynı logoyu taşıyan kutu, etiket, ambalaj veya kartları çoğaltma."
      : null,
    brand?.logoUrl && logoHandling === "reference"
      ? "Kurumsal logo referansı ayrıca verilecek. Logoyu AI sahne içinde tek kez, küçük ve doğal bir köşe imzası olarak yerleştirsin; büyük üst bant, dev tabela, sosyal medya kapağı veya geniş logo paneli yapma."
      : null,
    "Görselde rastgele yazı, sahte tipografi, watermark, bozuk ikon, sahte arayüz veya anlamsız detay üretme.",
    "Instagram, TikTok, platform adı, @kullanıcı adı, URL, hashtag veya sosyal medya hesabı yazma.",
    "Metin gerekiyorsa yalnız doğru Türkçe kullan; İngilizce, bozuk karakter, lorem ipsum ve anlamsız yazı kullanma.",
    "Aynı logo, marka etiketi, çıkartma veya rozet birden fazla kez görünmesin.",
    mode === "edit"
      ? "Ana özneyi, sahnenin kimliğini ve perspektifi tanınabilir bırak; sosyal medya için daha temiz ve profesyonel hale getir."
      : "Kompozisyon göze hoş gelen, dengeli, temiz ve yayınlanabilir olsun.",
    `Kullanıcı isteği: ${String(prompt || "").trim()}.`,
  ];

  return lines.filter(Boolean).join("\n");
}

function buildCampaignVideoPromptLines({ prompt, sceneLocked }) {
  const promptText = String(prompt || "").trim();
  const wantsPlainVideo = /ham video|düz video|stok video|belgesel/i.test(promptText);

  return [
    wantsPlainVideo
      ? null
      : "Düz stok video değil, yayınlanabilir kampanya veya tanıtım filmi estetiği üret.",
    sceneLocked
      ? "Mekânı değiştirmeden reklam hissini kamera dili, hareket ritmi, tipografik disiplin ve kurumsal yüzey detaylarıyla kur."
      : "Rastgele B-roll kolajı gibi durma; tek ana fikir etrafında akıcı reklam kompozisyonu kur.",
    "Ana ürün, hizmet veya anlatıcı ilk saniyelerde net biçimde anlaşılsın.",
    "Kurumsal renkleri doğal yüzey, ışık vurgusu ve grafik detay olarak kullan; dev renk blokları yapıştırma.",
    "Dev kampanya rozeti, sahte sosyal medya arayüzü, ekrana yapışmış logo paneli veya köşeye sıkışmış indirim sticker'ı üretme.",
  ].filter(Boolean);
}

function buildNativeSocialVideoPrompt({
  prompt,
  aspectRatio,
  brand,
  sceneLocked = false,
  presenterReferenceMode = false,
  selectedProducts = [],
}) {
  const selectedProductSummary = selectedProducts
    .map((item) => {
      const parts = [item.name];
      if (item.description) {
        parts.push(item.description);
      }
      if (item.price) {
        parts.push(`özellik/fiyat: ${item.price}`);
      }
      return parts.filter(Boolean).join(" | ");
    })
    .join(" || ");
  const visualReferenceProducts = selectedProducts.filter((item) => item.mediaId || item.url);
  const visualReferenceSummary = visualReferenceProducts
    .map((item) => item.name)
    .filter(Boolean)
    .join(", ");
  const campaignVideoLines = buildCampaignVideoPromptLines({
    prompt,
    sceneLocked,
  });

  return [
    "Sosyal medya için yayınlanabilir kısa reklam veya tanıtım videosu üret.",
    `Video oranı: ${aspectRatio}.`,
    brand?.name ? `Marka adı: ${brand.name}.` : null,
    brand?.sector ? `Marka sektörü: ${brand.sector}.` : null,
    brand?.description ? `Marka açıklaması: ${brand.description}.` : null,
    brand?.slogan ? `Marka sloganı: ${brand.slogan}.` : null,
    brand?.videoGuidance ? `Markanın video üretim yönergesi: ${brand.videoGuidance}.` : null,
    brand?.colors
      ? `Kurumsal renkler: ana=${brand.colors.primary}, ikincil=${brand.colors.secondary}, vurgu=${brand.colors.accent}.`
      : null,
    brand?.colors
      ? "Kurumsal renkleri zorunlu kimlik sistemi kabul et; ana rengi baskın atmosferde, ikincili denge alanlarında, vurgu rengini odak anlarında etkili kullan."
      : null,
    brand?.personality ? `Marka tonu: ${brand.personality}.` : null,
    ...campaignVideoLines,
    selectedProducts.length
      ? `Seçilen ürün/hizmetler: ${selectedProductSummary}.`
      : null,
    selectedProducts.length === 1
      ? "Videoyu seçilen tek ürün veya hizmet etrafında kur."
      : null,
    selectedProducts.length > 1
      ? "Videoyu yalnız seçilen ürün ve hizmetler etrafında kur; alakasız ürün veya teklif ekleme."
      : null,
    visualReferenceProducts.length
      ? `Seçilen ürün/hizmet görselleri ayrıca referans olarak verilecek: ${visualReferenceSummary}. Bu görselleri birebir ürün/hizmet kimliği kabul et; değiştirme, yeniden yorumlama veya başka ürün üretme.`
      : null,
    presenterReferenceMode
      ? "Yüklenen kişi görseli ayrıca referans olarak verilecek. Bu kişiyi video içindeki marka sunucusu, model veya anlatıcı olarak kullan; yüz formunu, saç stilini, ten tonunu, yaş algısını ve ayırt edici görünümü tutarlı koru."
      : null,
    presenterReferenceMode
      ? "Kişiyi sahneye doğal hareket, temiz yüz detayları ve gerçekçi ışıkla yerleştir; rastgele başka kişi, yüz deformasyonu, maske hissi veya kimlik değişimi üretme."
      : null,
    sceneLocked
      ? "Seçilen medya havuzu görseli videodaki gerçek mekândır; duvarı, tezgâhı, masa düzenini, ışığı, perspektifi ve genel sahne kimliğini bozma."
      : null,
    "Rastgele yazı, watermark, bozuk tipografi, sahte arayüz veya anlamsız detay üretme.",
    "Instagram, TikTok, platform adı, @kullanıcı adı, URL, hashtag veya sosyal medya hesabı yazma.",
    "Metin gerekiyorsa yalnız doğru Türkçe kullan; İngilizce, bozuk karakter ve anlamsız yazı kullanma.",
    `Kullanıcı isteği: ${String(prompt || "").trim()}.`,
  ]
    .filter(Boolean)
    .join("\n");
}

function StatusBadge({ connected }) {
  return connected ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
      <CheckCircle2 className="w-3 h-3" />
      Bağlı
    </span>
  ) : (
    <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500">
      Bağlı Değil
    </span>
  );
}

/* ─────────────────── Dashboard Tab ─────────────────── */
function DashboardContent() {
  const { activeBrand, brands, connectedProviders, providers, refreshAll, refreshing } =
    useSocialModule();
  const navigate = useNavigate();

  const warnings = useMemo(() => {
    const result = [];
    if (!connectedProviders.length)
      result.push("Henüz aktif sağlayıcı bağlı değil. API Ayarları sekmesinden anahtar girin.");
    if (!brands.length)
      result.push("Henüz marka tanımlanmadı. Marka Yönetimi sekmesinden ilk markayı oluşturun.");
    if (activeBrand && !activeBrand.description)
      result.push("Aktif markanın açıklaması eksik. Üretim kalitesi için kısa bir marka özeti ekleyin.");
    if (activeBrand && !activeBrand.slogan)
      result.push("Aktif markanın slogan alanı boş. Post çıktıları için doldurmanız iyi olur.");
    return result;
  }, [activeBrand, brands.length, connectedProviders.length]);

  const quickLinks = [
    { title: "API Ayarları", description: "OpenAI ve Gemini anahtarlarınızı bağlayın.", accent: "bg-blue-50 text-blue-700 border-blue-200", tabId: "settings" },
    { title: "Marka Yönetimi", description: "Marka tonu, logo ve ürün bilgilerini güncelleyin.", accent: "bg-emerald-50 text-emerald-700 border-emerald-200", tabId: "brand" },
    { title: "Görsel Oluştur", description: "Marka bağlamı ile hızlı görsel üretin.", accent: "bg-amber-50 text-amber-700 border-amber-200", tabId: "image" },
    { title: "Video Oluştur", description: "Yapay zeka ile kısa sosyal medya videoları başlatın.", accent: "bg-rose-50 text-rose-700 border-rose-200", tabId: "video" },
  ];

  const goToTab = (tabId) => navigate(`?tab=${tabId}`, { replace: true });

  return (
    <div className="p-4 md:p-6 space-y-5 overflow-y-auto h-full">
      {/* Stat bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Marka", value: brands.length },
          { label: "Bağlı Sağlayıcı", value: connectedProviders.length },
          { label: "Toplam Sağlayıcı", value: providers.length },
          { label: "Galeri Görseli", value: activeBrand?.gallery?.length || 0 },
        ].map((stat) => (
          <SectionCard key={stat.label} className="flex flex-col">
            <div className="text-xs uppercase tracking-widest text-gray-400 font-medium">{stat.label}</div>
            <div className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</div>
          </SectionCard>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.3fr_0.7fr]">
        {/* Active brand card */}
        <SectionCard>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600 mb-3">
                <Building2 className="w-3.5 h-3.5" />
                Aktif Marka
              </div>
              <h2 className="text-2xl font-bold text-gray-900 truncate">
                {activeBrand?.name || "Marka seçilmedi"}
              </h2>
              <p className="mt-2 text-sm text-gray-500 line-clamp-2">
                {activeBrand?.description || "Marka seçtiğinizde burada kısa özet görünecek."}
              </p>
              {activeBrand && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Slogan</div>
                    <div className="mt-1 text-sm font-medium text-gray-800 truncate">
                      {activeBrand.slogan || "—"}
                    </div>
                  </div>
                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Ürün / Hizmet</div>
                    <div className="mt-1 text-sm font-medium text-gray-800 truncate">
                      {activeBrand.products?.length
                        ? activeBrand.products.map((p) => p.name).join(", ")
                        : "—"}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => refreshAll(true)}
              className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 transition"
            >
              <RefreshCcw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Yenile
            </button>
          </div>
        </SectionCard>

        {/* Providers */}
        <SectionCard>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Sağlayıcı Durumu</h3>
          <div className="space-y-2">
            {providers.map((provider) => (
              <div
                key={provider.provider}
                className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5"
              >
                <div>
                  <div className="text-sm font-medium text-gray-800">{provider.providerName}</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {provider.isConnected ? provider.maskedApiKey || "Bağlı" : "Bağlantı kurulmadı"}
                  </div>
                </div>
                <StatusBadge connected={provider.isConnected} />
              </div>
            ))}
            {providers.length === 0 && (
              <div className="text-xs text-gray-400 text-center py-4">
                Hiç sağlayıcı bulunamadı.
              </div>
            )}
          </div>
        </SectionCard>
      </div>

      {/* Quick links */}
      <SectionCard>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Hızlı Erişim</h3>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {quickLinks.map((link) => (
            <button
              key={link.tabId}
              onClick={() => goToTab(link.tabId)}
              className="group rounded-xl border border-gray-200 bg-gray-50 p-4 text-left hover:bg-white hover:border-gray-300 transition"
            >
              <div className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${link.accent}`}>
                {link.title}
              </div>
              <p className="mt-2 text-xs text-gray-500 leading-relaxed">{link.description}</p>
              <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-gray-700">
                Aç
                <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          ))}
        </div>
      </SectionCard>

      {/* Warnings */}
      {warnings.length > 0 && (
        <SectionCard>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h3 className="text-sm font-semibold text-gray-700">Üretim Öncesi Kontrol</h3>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {warnings.map((w) => (
              <div
                key={w}
                className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800"
              >
                {w}
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

/* ─────────────────── Settings Tab ─────────────────── */
const createDraftMap = (providers) =>
  Object.fromEntries(
    providers.map((provider) => [
      provider.provider,
      {
        apiKey: "",
        defaultTextModel: provider.defaultTextModel || provider.models?.text?.[0] || "",
        defaultImageModel: provider.defaultImageModel || provider.models?.image?.[0] || "",
        defaultVideoModel: provider.defaultVideoModel || provider.models?.video?.[0] || "",
        isActive: provider.isConnected ? provider.isActive !== false : true,
      },
    ])
  );

function SettingsContent() {
  const { providers, refreshProviders } = useSocialModule();
  const [drafts, setDrafts] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  useEffect(() => {
    setDrafts(createDraftMap(providers));
  }, [providers]);

  const connectedCount = useMemo(
    () => providers.filter((p) => p.isConnected).length,
    [providers]
  );

  const updateDraft = (providerKey, field, value) => {
    setDrafts((prev) => ({ ...prev, [providerKey]: { ...prev[providerKey], [field]: value } }));
  };

  const showMessage = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
  };

  const saveProvider = async (provider) => {
    const draft = drafts[provider.provider];
    if (!draft?.apiKey?.trim()) {
      showMessage("Kaydetmek için API anahtarı girmeniz gerekiyor.", "error");
      return;
    }
    try {
      setBusyKey(`${provider.provider}-save`);
      showMessage("");
      await ApiService.upsertSocialProvider({
        provider: provider.provider,
        apiKey: draft.apiKey.trim(),
        defaultTextModel: draft.defaultTextModel || null,
        defaultImageModel: draft.defaultImageModel || null,
        defaultVideoModel: provider.capabilities?.video ? draft.defaultVideoModel || null : null,
        isActive: draft.isActive,
      });
      await refreshProviders();
      setDrafts((prev) => ({ ...prev, [provider.provider]: { ...prev[provider.provider], apiKey: "" } }));
      showMessage(`${provider.providerName} ayarları kaydedildi.`, "success");
    } catch (err) {
      showMessage(err?.message || "Sağlayıcı kaydedilemedi.", "error");
    } finally {
      setBusyKey("");
    }
  };

  const testProvider = async (provider) => {
    try {
      setBusyKey(`${provider.provider}-test`);
      showMessage("");
      await ApiService.testSocialProvider(provider.provider, {
        apiKey: drafts[provider.provider]?.apiKey?.trim() || undefined,
      });
      showMessage(`${provider.providerName} bağlantı testi başarılı.`, "success");
    } catch (err) {
      showMessage(err?.message || "Bağlantı testi başarısız.", "error");
    } finally {
      setBusyKey("");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5 overflow-y-auto h-full">
      {/* Header info */}
      <div className="grid gap-4 md:grid-cols-[1fr_auto]">
        <SectionCard className="flex items-start gap-4">
          <div className="inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
            <KeyRound className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-gray-900">Kendi API anahtarınızı kullanın</h2>
            <p className="mt-1 text-sm text-gray-500">
              Bu modül sistem ortak anahtarını kullanmaz. Her kullanıcı kendi sağlayıcı anahtarını bağlar.
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              {[
                { label: "Sağlayıcı", value: providers.length },
                { label: "Bağlı", value: connectedCount },
                { label: "Video Destekli", value: providers.filter((p) => p.capabilities?.video).length },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-center">
                  <div className="text-xs text-gray-400">{stat.label}</div>
                  <div className="text-lg font-bold text-gray-800">{stat.value}</div>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
        <SectionCard className="min-w-[200px]">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Notlar</h3>
          <ul className="space-y-2 text-xs text-gray-500">
            <li className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">Anahtar kaydedildikten sonra ham değer geri gösterilmez.</li>
            <li className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">Farklı sağlayıcılar metin, görsel ve video üretimini destekler.</li>
            <li className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2">Her araç için varsayılan model seçebilirsiniz.</li>
          </ul>
        </SectionCard>
      </div>

      {message && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            messageType === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : messageType === "error"
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-gray-200 bg-gray-50 text-gray-700"
          }`}
        >
          {message}
        </div>
      )}

      {/* Provider cards */}
      <div className="grid gap-4 xl:grid-cols-2">
        {providers.map((provider) => {
          const draft = drafts[provider.provider] || {};
          const isSaving = busyKey === `${provider.provider}-save`;
          const isTesting = busyKey === `${provider.provider}-test`;

          return (
            <SectionCard key={provider.provider}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-gray-900">{provider.providerName}</h3>
                    <StatusBadge connected={provider.isConnected} />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {provider.isConnected
                      ? `Kayıtlı anahtar: ${provider.maskedApiKey || "Bağlı"}`
                      : "Bu sağlayıcı için kendi API anahtarınızı ekleyin."}
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={draft.isActive ?? true}
                    onChange={(e) => updateDraft(provider.provider, "isActive", e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600"
                  />
                  Aktif
                </label>
              </div>

              <div className="space-y-3">
                <Field label="API Anahtarı">
                  <input
                    type="password"
                    value={draft.apiKey || ""}
                    onChange={(e) => updateDraft(provider.provider, "apiKey", e.target.value)}
                    placeholder={`${provider.providerName} anahtarınızı girin`}
                    className={inputCls}
                  />
                </Field>

                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Metin Modeli">
                    <select value={draft.defaultTextModel || ""} onChange={(e) => updateDraft(provider.provider, "defaultTextModel", e.target.value)} className={inputCls}>
                      {provider.models?.text?.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="Görsel Modeli">
                    <select value={draft.defaultImageModel || ""} onChange={(e) => updateDraft(provider.provider, "defaultImageModel", e.target.value)} className={inputCls}>
                      {provider.models?.image?.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="Video Modeli">
                  <select
                    value={draft.defaultVideoModel || ""}
                    disabled={!provider.capabilities?.video}
                    onChange={(e) => updateDraft(provider.provider, "defaultVideoModel", e.target.value)}
                    className={`${inputCls} disabled:bg-gray-100 disabled:text-gray-400`}
                  >
                    {!provider.capabilities?.video ? (
                      <option value="">Bu sağlayıcı video desteklemiyor</option>
                    ) : (
                      provider.models?.video?.map((m) => <option key={m} value={m}>{m}</option>)
                    )}
                  </select>
                </Field>

                <div className="flex flex-wrap gap-2 pt-1">
                  <button
                    onClick={() => testProvider(provider)}
                    disabled={isTesting}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition"
                  >
                    {isTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlugZap className="w-3.5 h-3.5" />}
                    Bağlantıyı Test Et
                  </button>
                  <button
                    onClick={() => saveProvider(provider)}
                    disabled={isSaving}
                    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60 transition"
                  >
                    {isSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Ayarları Kaydet
                  </button>
                </div>
              </div>
            </SectionCard>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────── Brand Tab ─────────────────── */
const emptyBrand = {
  name: "", sector: "", description: "", personality: "", slogan: "",
  videoGuidance: "",
  logoMediaId: "", logoUrl: "",
  colors: { primary: "#2563eb", secondary: "#0f172a", accent: "#f59e0b", background: "#ffffff", text: "#111827" },
  fonts: { heading: "Inter", body: "Inter" },
  socialAccounts: { instagram: "", tiktok: "", facebook: "", x: "", linkedin: "", youtube: "" },
  products: [], gallery: [], videos: [],
};

const toBrandMediaWriteItem = (item = {}) => ({
  mediaId: item.mediaId || "",
  url: item.url || "",
  description: item.description || "",
});

const toBrandProductWriteItem = (item = {}) => ({
  name: item.name || "",
  description: item.description || "",
  price: item.price || "",
  mediaId: item.mediaId || "",
  url: item.url || "",
});

const buildBrandWritePayload = (form) => ({
  name: form.name || "",
  sector: form.sector || "",
  description: form.description || "",
  personality: form.personality || "",
  slogan: form.slogan || "",
  videoGuidance: form.videoGuidance || "",
  logoMediaId: form.logoMediaId || "",
  colors: { ...(form.colors || {}) },
  fonts: { ...(form.fonts || {}) },
  socialAccounts: { ...(form.socialAccounts || {}) },
  products: Array.isArray(form.products)
    ? form.products.map(toBrandProductWriteItem).filter((item) => item.name?.trim())
    : [],
  gallery: Array.isArray(form.gallery)
    ? form.gallery.map(toBrandMediaWriteItem).filter((item) => item.url || item.mediaId)
    : [],
  videos: Array.isArray(form.videos)
    ? form.videos.map(toBrandMediaWriteItem).filter((item) => item.url || item.mediaId)
    : [],
});

const colorLabels = { primary: "Ana Renk", secondary: "İkincil Renk", accent: "Vurgu Rengi", background: "Arka Plan", text: "Metin" };
const socialLabels = { instagram: "Instagram", tiktok: "TikTok", facebook: "Facebook", x: "X / Twitter", linkedin: "LinkedIn", youtube: "YouTube" };

function BrandContent() {
  const { activeBrandId, brands, refreshBrands, setActiveBrandId } = useSocialModule();
  const [form, setForm] = useState(emptyBrand);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");
  const [analyzingLogoColors, setAnalyzingLogoColors] = useState(false);
  const [suggestedPalette, setSuggestedPalette] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  useEffect(() => {
    setSelectedBrandId(activeBrandId || brands[0]?.id || "");
  }, [activeBrandId, brands]);

  useEffect(() => {
    const selected = brands.find((b) => b.id === selectedBrandId);
    if (!selected) { setForm(emptyBrand); setSuggestedPalette([]); return; }
    setForm({
      ...emptyBrand, ...selected,
      colors: { ...emptyBrand.colors, ...(selected.colors || {}) },
      fonts: { ...emptyBrand.fonts, ...(selected.fonts || {}) },
      socialAccounts: { ...emptyBrand.socialAccounts, ...(selected.socialAccounts || {}) },
      videoGuidance: selected.videoGuidance || "",
      products: selected.products || [],
      gallery: selected.gallery || [],
      videos: selected.videos || [],
    });
    setSuggestedPalette([]);
  }, [brands, selectedBrandId]);

  const showMsg = (text, type = "info") => { setMessage(text); setMessageType(type); };
  const isEditing = Boolean(selectedBrandId);
  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateNested = (group, field, value) => setForm((prev) => ({ ...prev, [group]: { ...(prev[group] || {}), [field]: value } }));
  const updateProduct = (i, field, value) => setForm((prev) => ({ ...prev, products: prev.products.map((item, idx) => idx === i ? { ...item, [field]: value } : item) }));
  const updateMedia = (type, i, field, value) => setForm((prev) => ({ ...prev, [type]: prev[type].map((item, idx) => idx === i ? { ...item, [field]: value } : item) }));

  const applyAnalyzedColors = (analysisResult) => {
    if (!analysisResult?.colors) return;
    setForm((prev) => ({
      ...prev,
      colors: {
        ...(prev.colors || {}),
        ...analysisResult.colors,
      },
    }));
    setSuggestedPalette(Array.isArray(analysisResult.palette) ? analysisResult.palette : []);
  };

  const analyzeLogoColors = async (payload, successMessage = "Logo renkleri uygulandı. İstersen düzenleyebilirsin.") => {
    try {
      setAnalyzingLogoColors(true);
      const analysis = await ApiService.analyzeSocialBrandColors(payload);
      applyAnalyzedColors(analysis);
      showMsg(successMessage, "success");
    } catch (err) {
      showMsg(
        err?.message || "Logo renkleri çözümlenemedi. Backend logunu kontrol et.",
        "error"
      );
    } finally {
      setAnalyzingLogoColors(false);
    }
  };

  const uploadAsset = async (file, type, index = null) => {
    if (!file) return;
    try {
      const key = `${type}-${index ?? "single"}`;
      setUploadingKey(key);
      const uploaded = await ApiService.uploadSocialMedia(file);
      if (type === "logo") {
        setForm((prev) => ({ ...prev, logoMediaId: uploaded.id, logoUrl: uploaded.url }));
        await analyzeLogoColors(
          { mediaId: uploaded.id },
          "Logo yüklendi. Kurumsal kimlik renkleri logoya göre dolduruldu."
        );
      } else if (type === "products") {
        updateProduct(index, "mediaId", uploaded.id);
        updateProduct(index, "url", uploaded.url);
        showMsg("Medya yüklendi.", "success");
      } else {
        updateMedia(type, index, "mediaId", uploaded.id);
        updateMedia(type, index, "url", uploaded.url);
        showMsg("Medya yüklendi.", "success");
      }
    } catch (err) {
      showMsg(err?.message || "Medya yüklenemedi.", "error");
    } finally {
      setUploadingKey("");
    }
  };

  const saveBrand = async () => {
    if (!form.name.trim()) { showMsg("Marka adı zorunlu.", "error"); return; }
    try {
      setSaving(true); showMsg("");
      const payload = buildBrandWritePayload(form);
      const response = isEditing
        ? await ApiService.updateSocialBrand(selectedBrandId, payload)
        : await ApiService.createSocialBrand(payload);
      await refreshBrands();
      setSelectedBrandId(response.id);
      setActiveBrandId(response.id);
      showMsg("Marka kaydedildi.", "success");
    } catch (err) {
      showMsg(err?.message || "Marka kaydedilemedi.", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteBrand = async () => {
    if (!selectedBrandId) return;
    try {
      setDeleting(true); showMsg("");
      await ApiService.deleteSocialBrand(selectedBrandId);
      await refreshBrands();
      setSelectedBrandId("");
      setForm(emptyBrand);
      showMsg("Marka silindi.", "success");
    } catch (err) {
      showMsg(err?.message || "Marka silinemedi.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 overflow-y-auto h-full max-w-[1400px] mx-auto">
      <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
        {/* Modern Brand Sidebar */}
        <div className="space-y-4">
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)]">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
                  <Building2 className="w-4 h-4" />
                </div>
                <h2 className="text-base font-semibold text-gray-900 tracking-tight">Katalog</h2>
              </div>
              <button
                onClick={() => { setSelectedBrandId(""); setForm(emptyBrand); showMsg(""); }}
                className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-100 transition-colors shadow-[0_2px_10px_-4px_rgba(79,70,229,0.3)]"
              >
                <Plus className="w-3.5 h-3.5" /> Yeni
              </button>
            </div>
            
            <div className="space-y-2">
              {brands.length === 0 ? (
                <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50/50 py-8 px-4 flex flex-col items-center justify-center text-center">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm mb-3">
                    <Grid2X2 className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600">Henüz marka yok</p>
                  <p className="text-xs text-gray-400 mt-1">İlk markanızı oluşturmak için &quot;Yeni&quot; tuşuna tıklayın.</p>
                </div>
              ) : (
                brands.map((brand) => (
                  <button
                    key={brand.id}
                    onClick={() => { setSelectedBrandId(brand.id); setActiveBrandId(brand.id); }}
                    className={`w-full group relative flex items-center gap-3 rounded-xl p-3 text-left transition-all duration-200 ${
                      selectedBrandId === brand.id
                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-indigo-200"
                        : "bg-white hover:bg-gray-50 border border-gray-100 text-gray-700 hover:border-gray-200"
                    }`}
                  >
                    {brand.logoUrl ? (
                      <img src={brand.logoUrl} alt="" className="w-10 h-10 rounded-lg object-cover bg-white shrink-0 border border-black/5" />
                    ) : (
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-medium text-sm ${selectedBrandId === brand.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                        {brand.name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-semibold truncate ${selectedBrandId === brand.id ? "text-white" : "text-gray-900"}`}>{brand.name}</div>
                      <div className={`text-xs mt-0.5 truncate ${selectedBrandId === brand.id ? "text-blue-100" : "text-gray-500"}`}>
                        {brand.sector || "Sektör belirtilmedi"}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Brand Editor */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.05)] overflow-hidden flex flex-col">
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-white/50">
            <div>
              <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                {isEditing ? "Marka Profilini Düzenle" : "Yeni Marka Oluştur"}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Yapay zeka üretimlerinde kullanılacak kurumsal kimlik yönergelerini belirleyin.
              </p>
            </div>
            <div className="flex gap-2">
              {isEditing && (
                <button
                  onClick={deleteBrand}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 rounded-xl bg-red-50 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-60 transition"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  <span className="hidden sm:inline">Markayı Sil</span>
                </button>
              )}
              <button
                onClick={saveBrand}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-gray-900 px-5 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60 transition shadow-sm"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Ayarları Kaydet
              </button>
            </div>
          </div>

          <div className="p-6 overflow-y-auto">
            {message && (
              <div className={`mb-6 flex items-center gap-3 rounded-xl border p-4 text-sm font-medium ${
                messageType === "success" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                  : messageType === "error" ? "border-red-200 bg-red-50 text-red-800"
                  : "border-blue-200 bg-blue-50 text-blue-800"
              }`}>
                {messageType === "success" && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}
                {messageType === "error" && <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />}
                {message}
              </div>
            )}

            <div className="space-y-10">
              {/* Section 1: Core Info */}
              <section>
                <div className="flex items-center gap-2 mb-5">
                  <Building2 className="w-5 h-5 text-gray-400" />
                  <h4 className="font-semibold text-gray-900">Temel Bilgiler</h4>
                  <div className="h-px bg-gray-200 flex-1 ml-4" />
                </div>
                <div className="grid gap-5 md:grid-cols-2">
                  <div className="col-span-full flex items-start gap-6">
                    <div className="shrink-0 flex flex-col items-center gap-2">
                       <label className="group relative flex w-24 h-24 cursor-pointer items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-all overflow-hidden">
                          {uploadingKey === "logo-single" || analyzingLogoColors ? <Loader2 className="w-6 h-6 animate-spin text-gray-400" /> :
                            form.logoUrl ? <img src={form.logoUrl} alt="Logo" className="w-full h-full object-cover" /> :
                            <ImagePlus className="w-6 h-6 text-gray-400 group-hover:scale-110 transition-transform" />}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset(e.target.files?.[0], "logo")} />
                       </label>
                       <span className="text-xs font-medium text-gray-500">Logo (Önerilen)</span>
                    </div>
                    <div className="flex-1 space-y-4">
                      <Field label="Marka Adı"><input type="text" value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Örn: Acme Corp" className={inputCls} /></Field>
                      <Field label="Sektör"><input type="text" value={form.sector} onChange={(e) => updateField("sector", e.target.value)} placeholder="Örn: Teknoloji" className={inputCls} /></Field>
                    </div>
                  </div>
                  
                  <div className="col-span-full">
                    <Field label="Marka Özeti">
                      <textarea value={form.description} onChange={(e) => updateField("description", e.target.value)} rows={3} placeholder="Markanız ne yapar, hedef kitlesi kimdir? Yapay zeka bu bağlamı kullanacak." className={inputCls} />
                    </Field>
                  </div>
                  <Field label="Kişilik / Ton">
                    <input type="text" value={form.personality} onChange={(e) => updateField("personality", e.target.value)} placeholder="Örn: Samimi, Profesyonel, Eğlenceli" className={inputCls} />
                  </Field>
                  <Field label="Slogan / Motto">
                    <input type="text" value={form.slogan} onChange={(e) => updateField("slogan", e.target.value)} placeholder="Örn: Gelecek burada." className={inputCls} />
                  </Field>
                </div>
              </section>

              {/* Section 2: Visual Identity */}
              <section>
                <div className="flex items-center gap-2 mb-5">
                  <Palette className="w-5 h-5 text-gray-400" />
                  <h4 className="font-semibold text-gray-900">Kurumsal Kimlik</h4>
                  <div className="h-px bg-gray-200 flex-1 ml-4" />
                </div>
                
                <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100">
                  <h5 className="text-sm font-semibold text-gray-700 mb-4">Ana Renk Paleti</h5>
                  <div className="flex flex-wrap gap-4">
                    {Object.entries(form.colors || {}).map(([key, value]) => (
                      <div key={key} className="flex-1 min-w-[120px]">
                        <label className="block text-xs font-medium text-gray-500 mb-2">{colorLabels[key] || key}</label>
                        <div className="relative group cursor-pointer">
                          <div className="absolute inset-y-1 left-1 w-8 rounded shadow-sm border border-black/10 pointer-events-none" style={{ backgroundColor: value || "#000" }} />
                          <input type="color" value={value || "#000000"} onChange={(e) => updateNested("colors", key, e.target.value)} className="absolute opacity-0 w-8 h-full cursor-pointer left-1" />
                          <input type="text" value={value || ""} onChange={(e) => updateNested("colors", key, e.target.value)} className={`pl-11 uppercase font-mono ${inputCls}`} placeholder="#000000" />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <h6 className="text-sm font-semibold text-gray-800">Logodan otomatik renk önerisi</h6>
                        <p className="mt-1 text-xs text-gray-500">
                          Logo yüklenince otomatik çalışır. İstersen tekrar analiz ettirebilirsin.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          analyzeLogoColors(
                            { mediaId: form.logoMediaId || null, url: form.logoUrl || null },
                            "Logo yeniden analiz edildi. Renk alanları güncellendi."
                          )
                        }
                        disabled={analyzingLogoColors || (!form.logoMediaId && !form.logoUrl)}
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-60 transition"
                      >
                        {analyzingLogoColors ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Palette className="w-3.5 h-3.5" />}
                        Logodan Renk Öner
                      </button>
                    </div>

                    {suggestedPalette.length > 0 ? (
                      <div className="mt-4 flex flex-wrap gap-3">
                        {suggestedPalette.map((item) => (
                          <div key={item.hex} className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
                            <span className="h-7 w-7 rounded-lg border border-black/10" style={{ backgroundColor: item.hex }} />
                            <div>
                              <div className="text-xs font-semibold text-gray-800 uppercase">{item.hex}</div>
                              <div className="text-[11px] text-gray-500">Ağırlık: {item.count}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-4 text-xs text-gray-500">
                        Önerilen palet burada görünecek.
                      </div>
                    )}
                  </div>

                  <div className="h-px bg-gray-200 w-full my-6" />

                  <div className="flex items-center gap-2 mb-4">
                    <Type className="w-4 h-4 text-gray-400" />
                    <h5 className="text-sm font-semibold text-gray-700">Tipografi Yönergesi</h5>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Field label="Başlık Fontu">
                      <input type="text" value={form.fonts.heading} onChange={(e) => updateNested("fonts", "heading", e.target.value)} placeholder="Örn: Inter, sans-serif" className={inputCls} />
                    </Field>
                    <Field label="Gövde Fontu">
                      <input type="text" value={form.fonts.body} onChange={(e) => updateNested("fonts", "body", e.target.value)} placeholder="Örn: Roboto" className={inputCls} />
                    </Field>
                  </div>
                </div>
              </section>

              {/* Section 3: Social & Links */}
              <section>
                <div className="flex items-center gap-2 mb-5">
                  <Share2 className="w-5 h-5 text-gray-400" />
                  <h4 className="font-semibold text-gray-900">Sosyal Bağlantılar</h4>
                  <div className="h-px bg-gray-200 flex-1 ml-4" />
                </div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {Object.entries(form.socialAccounts || {}).map(([key, value]) => (
                    <div key={key} className="relative">
                       <label className="text-xs font-medium text-gray-700 block mb-1.5">{socialLabels[key] || key}</label>
                       <input type="text" value={value} onChange={(e) => updateNested("socialAccounts", key, e.target.value)} placeholder={`@kullaniciadi`} className={inputCls} />
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-5">
                  <Clapperboard className="w-5 h-5 text-gray-400" />
                  <h4 className="font-semibold text-gray-900">Video Üretim Yönergesi</h4>
                  <div className="h-px bg-gray-200 flex-1 ml-4" />
                </div>
                <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-5">
                  <Field label="Markaya Özel Video Notu">
                    <textarea
                      value={form.videoGuidance}
                      onChange={(e) => updateField("videoGuidance", e.target.value)}
                      rows={4}
                      placeholder="Örn: Tanıtım videolarında mekânı hiç bozma. Kişi fotoğrafı verilirse o kişiyi doğal marka sunucusu gibi kullan. Düz stok video yerine rafine reklam hissi ver."
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                  <div className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-3 text-xs text-slate-600">
                    Bu alan yalnız video üretim promptuna eklenir. Tanıtım filmi, ürün demosu ve sahne koruma gibi video özel kuralları buradan tanımlayabilirsin.
                  </div>
                </div>
              </section>

              {/* Section 4: Products */}
              <section>
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-gray-400" />
                    <h4 className="font-semibold text-gray-900">Ürün / Hizmet Listesi</h4>
                  </div>
                  <button
                    onClick={() => setForm((prev) => ({ ...prev, products: [...prev.products, { name: "", description: "", price: "", mediaId: "", url: "" }] }))}
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-100 transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Yeni Ekle
                  </button>
                </div>
                
                <div className="space-y-4">
                  {form.products.map((product, index) => (
                    <div key={`product-${index}`} className="flex flex-col sm:flex-row gap-4 p-5 rounded-2xl border border-gray-200 bg-white shadow-sm group">
                      <div className="shrink-0 flex flex-col items-center gap-2">
                       <label className="relative flex w-16 h-16 cursor-pointer items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors overflow-hidden">
                          {uploadingKey === `products-${index}` ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> :
                            product.url ? <img src={product.url} alt={product.name} className="w-full h-full object-cover" /> :
                            <ImagePlus className="w-4 h-4 text-gray-400" />}
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAsset(e.target.files?.[0], "products", index)} />
                       </label>
                      </div>
                      <div className="flex-1 grid gap-4 md:grid-cols-[1fr_minmax(100px,2fr)]">
                        <div className="space-y-4">
                          <Field label="Ürün Adı"><input type="text" value={product.name} onChange={(e) => updateProduct(index, "name", e.target.value)} className={inputCls} placeholder="Örn: Premium Paket" /></Field>
                          <Field label="Fiyat/Özellik"><input type="text" value={product.price} onChange={(e) => updateProduct(index, "price", e.target.value)} className={inputCls} placeholder="99.99₺ veya Özellik" /></Field>
                        </div>
                        <div className="flex flex-col h-full">
                           <Field label="Detaylı Açıklama">
                             <textarea value={product.description} onChange={(e) => updateProduct(index, "description", e.target.value)} className={`${inputCls} resize-none h-[116px]`} placeholder="Bu ürünün öne çıkan özelliği nedir?" />
                           </Field>
                        </div>
                      </div>
                      <button
                        onClick={() => setForm((prev) => ({ ...prev, products: prev.products.filter((_, i) => i !== index) }))}
                        className="opacity-0 group-hover:opacity-100 shrink-0 self-start p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                        title="Kaldır"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {form.products.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 py-8 text-center">
                      <Package className="w-8 h-8 text-gray-400 mx-auto mb-2 opacity-50" />
                      <p className="text-sm font-medium text-gray-600">Ürün Eklenmedi</p>
                      <p className="text-xs text-gray-400 mt-1">Sistem promptlarına eklenmesi için en az bir ürün ekleyin.</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Section 5: Gallery & Video Context */}
              <section>
                <div className="flex items-center gap-2 mb-5">
                  <ImageIcon className="w-5 h-5 text-gray-400" />
                  <h4 className="font-semibold text-gray-900">Medya Havuzu</h4>
                  <div className="h-px bg-gray-200 flex-1 ml-4" />
                </div>
                <div className="grid gap-6 xl:grid-cols-2">
                  <MediaCollectionSection title="Görüntü Galeri" description="Sistem bu görselleri marka ile birlikte referans olarak kullanır." type="gallery" accept="image/*" items={form.gallery} icon={ImagePlus}
                    onAdd={() => setForm((prev) => ({ ...prev, gallery: [...prev.gallery, { mediaId: "", url: "" }] }))}
                    onUpload={uploadAsset}
                    onRemove={(i) => setForm((prev) => ({ ...prev, gallery: prev.gallery.filter((_, idx) => idx !== i) }))}
                    uploadingKey={uploadingKey}
                  />

                  <MediaCollectionSection title="Video Referansları" description="Sistem bu videoları marka diliyle birlikte referans olarak kullanır." type="videos" accept="video/*" items={form.videos} icon={Video}
                    onAdd={() => setForm((prev) => ({ ...prev, videos: [...prev.videos, { mediaId: "", url: "" }] }))}
                    onUpload={uploadAsset}
                    onRemove={(i) => setForm((prev) => ({ ...prev, videos: prev.videos.filter((_, idx) => idx !== i) }))}
                    uploadingKey={uploadingKey}
                  />
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MediaCollectionSection({ title, description, type, accept, items, icon: Icon, onAdd, onUpload, onRemove, uploadingKey }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <div>
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          <div className="text-xs text-gray-500 mt-0.5">{description}</div>
        </div>
        <button
          onClick={onAdd}
          className="shrink-0 p-2 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors shadow-sm"
          title="Dosya Ekle"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 space-y-3 overflow-y-auto max-h-[400px]">
        {items.map((item, index) => (
          <div key={`${type}-${index}`} className="group flex flex-col gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-sm hover:border-gray-300 transition-colors">
            <div className="flex items-center gap-3">
               <label className="relative shrink-0 flex w-12 h-12 cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors overflow-hidden">
                  {uploadingKey === `${type}-${index}` ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" /> :
                    item.url ? (
                      type === "videos"
                        ? <video src={item.url} className="w-full h-full object-cover" />
                        : <img src={item.url} alt="Referans" className="w-full h-full object-cover" />
                    ) : <Icon className="w-4 h-4 text-gray-400" />
                  }
                  <input type="file" accept={accept} className="hidden" onChange={(e) => onUpload(e.target.files?.[0], type, index)} />
               </label>
               <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-900">
                    {type === "videos" ? `Video ${index + 1}` : `Görsel ${index + 1}`}
                  </div>
                  <div className="truncate text-xs text-gray-500">
                    Yapay zeka bunu marka verileriyle birlikte otomatik yorumlar.
                  </div>
               </div>
               <button
                  onClick={() => onRemove(index)}
                  className="opacity-0 group-hover:opacity-100 shrink-0 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all"
                >
                  <Trash2 className="w-4 h-4" />
               </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-center py-6">
            <div className="inline-flex w-10 h-10 items-center justify-center rounded-full bg-gray-50 mb-2">
              <Icon className="w-5 h-5 text-gray-400" />
            </div>
            <p className="text-xs text-gray-500 font-medium">Burası boş</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────── Image Tab ─────────────────── */
function ImageContent() {
  const { activeBrandId, brands, connectedProviders } = useSocialModule();
  const availableProviders = useMemo(
    () => buildProviderOptions(connectedProviders, "image"),
    [connectedProviders]
  );
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [brandId, setBrandId] = useState(activeBrandId || "");
  const [contentSize, setContentSize] = useState("post");
  const [quality, setQuality] = useState("hq");
  const [logoHandling, setLogoHandling] = useState("none");
  const [prompt, setPrompt] = useState("");
  const [selectedProductKeys, setSelectedProductKeys] = useState([]);
  const [selectedPromptReference, setSelectedPromptReference] = useState("");
  const [personReferenceImage, setPersonReferenceImage] = useState("");
  const [personReferenceName, setPersonReferenceName] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [uploadedImage, setUploadedImage] = useState("");
  const [uploadedImageName, setUploadedImageName] = useState("");
  const [overlayText, setOverlayText] = useState("");
  const [overlaySubtext, setOverlaySubtext] = useState("");
  const [showLogo, setShowLogo] = useState(true);
  const [loading, setLoading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    const first = availableProviders[0];
    if (!provider && first) {
      setProvider(first.provider);
      setModel(getDefaultModelForCapability(first, "image"));
    }
  }, [availableProviders, provider]);

  useEffect(() => {
    setBrandId(activeBrandId || "");
  }, [activeBrandId]);

  const selectedProvider = useMemo(
    () => availableProviders.find((item) => item.provider === provider) || null,
    [availableProviders, provider]
  );
  const selectedBrand = useMemo(
    () => brands.find((item) => item.id === brandId) || null,
    [brandId, brands]
  );
  const availableProducts = useMemo(
    () =>
      (selectedBrand?.products || []).map((item, index) => ({
        ...item,
        selectionKey: `product-${index}`,
      })),
    [selectedBrand]
  );
  const selectedProducts = useMemo(
    () =>
      availableProducts.filter((item) => selectedProductKeys.includes(item.selectionKey)),
    [availableProducts, selectedProductKeys]
  );
  const selectedProductPayload = useMemo(
    () =>
      selectedProducts.map((item) => ({
        name: item.name,
        description: item.description || "",
        price: item.price || "",
        mediaId: item.mediaId || "",
        url: item.url || "",
      })),
    [selectedProducts]
  );
  const selectedPromptReferenceItem = useMemo(
    () =>
      selectedBrand?.gallery?.find((item) => item.url === selectedPromptReference) ||
      null,
    [selectedBrand, selectedPromptReference]
  );
  const selectedSizeMeta = useMemo(
    () => getImageContentSize(contentSize),
    [contentSize]
  );
  const canEditWithImage = provider === "gemini";
  const hasBrandLogo = Boolean(selectedBrand?.logoUrl);
  const hasSelectedProductVisualReference = useMemo(
    () => selectedProducts.some((item) => item.mediaId || item.url),
    [selectedProducts]
  );

  useEffect(() => {
    if (
      selectedPromptReference &&
      !selectedBrand?.gallery?.some((item) => item.url === selectedPromptReference)
    ) {
      setSelectedPromptReference("");
    }
  }, [selectedBrand, selectedPromptReference]);

  useEffect(() => {
    setSelectedProductKeys((prev) =>
      prev.filter((key) => availableProducts.some((item) => item.selectionKey === key))
    );
  }, [availableProducts]);

  useEffect(() => {
    if (!hasBrandLogo) {
      setLogoHandling("none");
      return;
    }

    setLogoHandling("reference");
  }, [hasBrandLogo]);

  useEffect(() => {
    const nextLogoHandling = selectedImage?.brandIdentity?.logoHandling;
    setShowLogo(
      Boolean(selectedImage?.brand?.logoUrl) &&
        nextLogoHandling === "export" &&
        !selectedImage?.brandIdentity?.logoApplied
    );
  }, [selectedImage]);

  const resetPromptForm = () => {
    setPrompt("");
    setSelectedProductKeys([]);
    setSelectedPromptReference("");
    setPersonReferenceImage("");
    setPersonReferenceName("");
    setError("");
  };

  const resetEditForm = () => {
    setUploadedImage("");
    setUploadedImageName("");
    setEditPrompt("");
    setError("");
  };

  const pushGeneratedImage = (response, sourcePrompt, sourceMode) => {
    const nextImage = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      url: response.url,
      prompt: sourcePrompt,
      contentSize,
      sizeLabel: selectedSizeMeta.label,
      provider: response.provider,
      model: response.model,
      generatedContentId: response.generatedContentId,
      mediaId: response.mediaId,
      mimeType: response.mimeType,
      quality: response.quality || quality,
      sourceMode,
      createdAt: new Date().toISOString(),
      brandIdentity: response.brandIdentity || null,
      selectedProducts: selectedProductPayload,
      referenceImage: sourceMode === "generate" && selectedPromptReferenceItem
        ? {
            url: selectedPromptReferenceItem.url,
            description: selectedPromptReferenceItem.description || "",
          }
        : null,
      brand: selectedBrand
        ? {
            id: selectedBrand.id,
            name: selectedBrand.name,
            logoUrl: selectedBrand.logoUrl || null,
            colors: selectedBrand.colors || null,
          }
        : null,
    };

    setGeneratedImages((prev) => [nextImage, ...prev]);
    setSelectedImage(nextImage);
  };

  const handlePromptGenerate = async () => {
    if (!provider || !model) {
      setError("Sağlayıcı ve model zorunlu.");
      return;
    }
    if (!prompt.trim()) {
      setError("Lütfen bir üretim prompt’u girin.");
      return;
    }
    const sceneReferenceImage = selectedPromptReference || "";
    const sceneLocked = Boolean(sceneReferenceImage);
    const referenceImages = [];

    if (sceneLocked && provider !== "gemini") {
      setError("Referans görselli üretim şu anda yalnız desteklenen görsel modelleriyle çalışıyor.");
      return;
    }
    if (personReferenceImage && provider !== "gemini") {
      setError("Yüklenen kişi/görsel referansını kullanma şu anda yalnız Gemini görsel modellerinde çalışıyor.");
      return;
    }
    if (hasBrandLogo && provider !== "gemini") {
      setError("Marka logosu AI tarafından yerleştirileceği için bu akışta Gemini görsel modeli seçilmelidir.");
      return;
    }
    if (hasSelectedProductVisualReference && provider !== "gemini") {
      setError("Seçilen ürün/hizmet görsellerini birebir referansla kullanmak şu anda yalnız Gemini görsel modellerinde çalışıyor.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await ApiService.generateSocialImage({
        provider,
        model,
        prompt: buildNativeSocialImagePrompt({
          mode: sceneLocked ? "edit" : "generate",
          prompt,
          contentSize,
          quality,
          logoHandling,
          brand: selectedBrand,
          sceneLocked,
          personReferenceMode: Boolean(personReferenceImage),
          selectedProducts,
        }),
        originalPrompt: prompt.trim(),
        brandId: brandId || null,
        size: selectedSizeMeta.ratio,
        quality,
        logoHandling,
        referenceImages,
        personReferenceImage: personReferenceImage || undefined,
        imageData: sceneReferenceImage || undefined,
        sceneLocked,
        selectedProducts: selectedProductPayload,
      });
      pushGeneratedImage(response, prompt.trim(), "generate");
    } catch (err) {
      setError(err?.message || "Görsel üretilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setError("");
      const dataUrl = await readFileAsDataUrl(file);
      setUploadedImage(dataUrl);
      setUploadedImageName(file.name || "Yüklenen görsel");
    } catch (err) {
      setError(err?.message || "Görsel yüklenemedi.");
    }
  };

  const handlePersonReferenceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setError("");
      const dataUrl = await readFileAsDataUrl(file);
      setPersonReferenceImage(dataUrl);
      setPersonReferenceName(file.name || "Kişi referansı");
    } catch (err) {
      setError(err?.message || "Kişi referansı yüklenemedi.");
    }
  };

  const handleEditGenerate = async () => {
    if (!provider || !model) {
      setError("Sağlayıcı ve model zorunlu.");
      return;
    }
    if (!uploadedImage) {
      setError("Önce düzenlemek için bir görsel yükleyin.");
      return;
    }
    if (!editPrompt.trim()) {
      setError("Yüklenen görsel için düzenleme yönlendirmesi yazın.");
      return;
    }
    if (!canEditWithImage) {
      setError("Yüklenen görselle düzenleme yalnız desteklenen modellerde çalışıyor.");
      return;
    }
    if (hasBrandLogo && provider !== "gemini") {
      setError("Marka logosu AI tarafından yerleştirileceği için bu akışta Gemini görsel modeli seçilmelidir.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const response = await ApiService.generateSocialImage({
        provider,
        model,
        prompt: buildNativeSocialImagePrompt({
          mode: "edit",
          prompt: editPrompt,
          contentSize,
          quality,
          logoHandling,
          brand: selectedBrand,
          selectedProducts,
        }),
        originalPrompt: editPrompt.trim(),
        brandId: brandId || null,
        size: selectedSizeMeta.ratio,
        quality,
        logoHandling,
        imageData: uploadedImage,
        selectedProducts: selectedProductPayload,
      });
      pushGeneratedImage(response, editPrompt.trim(), "edit");
    } catch (err) {
      setError(err?.message || "Görsel düzenlenemedi.");
    } finally {
      setLoading(false);
    }
  };

  const downloadCanvas = (fileSize = contentSize) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const link = document.createElement("a");
    link.download = `social-${fileSize}-${Date.now()}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const handleExport = async () => {
    if (!selectedImage) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    try {
      setError("");
      const exportSize = getImageContentSize(selectedImage.contentSize);
      const exportBrand = selectedImage.brand || null;
      canvas.width = exportSize.width;
      canvas.height = exportSize.height;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const baseImage = await loadCanvasImage(selectedImage.url);
      drawImageCover(ctx, baseImage, canvas.width, canvas.height);

      if (overlayText.trim() || overlaySubtext.trim()) {
        const panelHeight = overlaySubtext.trim()
          ? Math.round(canvas.height * 0.16)
          : Math.round(canvas.height * 0.11);
        const panelWidth = Math.round(canvas.width * 0.84);
        const panelX = Math.round((canvas.width - panelWidth) / 2);
        const panelY = canvas.height - panelHeight - Math.round(canvas.height * 0.08);

        ctx.fillStyle = "rgba(17, 24, 39, 0.56)";
        ctx.fillRect(panelX, panelY, panelWidth, panelHeight);
        ctx.textAlign = "center";
        ctx.fillStyle = "#ffffff";
        ctx.font = `700 ${Math.round(canvas.width * 0.052)}px Inter, sans-serif`;
        ctx.fillText(
          overlayText.trim(),
          canvas.width / 2,
          panelY + Math.round(panelHeight * 0.46)
        );

        if (overlaySubtext.trim()) {
          ctx.fillStyle =
            exportBrand?.colors?.primary || exportBrand?.colors?.accent || "#ffffff";
          ctx.font = `500 ${Math.round(canvas.width * 0.028)}px Inter, sans-serif`;
          ctx.fillText(
            overlaySubtext.trim(),
            canvas.width / 2,
            panelY + Math.round(panelHeight * 0.78)
          );
        }
      }

      if (showLogo && exportBrand?.logoUrl) {
        try {
          const logoImage = await loadCanvasImage(exportBrand.logoUrl);
          const logoMaxWidth = Math.round(canvas.width * 0.18);
          const logoMaxHeight = Math.round(canvas.height * 0.08);
          const logoScale = Math.min(
            logoMaxWidth / (logoImage.width || logoMaxWidth),
            logoMaxHeight / (logoImage.height || logoMaxHeight),
            1
          );
          const logoWidth = Math.round((logoImage.width || logoMaxWidth) * logoScale);
          const logoHeight = Math.round((logoImage.height || logoMaxHeight) * logoScale);
          ctx.drawImage(
            logoImage,
            canvas.width - logoWidth - 32,
            32,
            logoWidth,
            logoHeight
          );
        } catch {}
      }

      downloadCanvas(selectedImage.contentSize);
    } catch (err) {
      setError(err?.message || "PNG dışa aktarılamadı.");
    }
  };

  return (
    <div className="p-4 md:p-6 overflow-y-auto h-full">
      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard>
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
              <ImageIcon className="w-4.5 h-4.5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Görsel Oluştur</h2>
            
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Sağlayıcı">
                <select value={provider} onChange={(e) => { const p = availableProviders.find((x) => x.provider === e.target.value); setProvider(e.target.value); setModel(getDefaultModelForCapability(p, "image")); }} className={inputCls}>
                  <option value="">Seçin</option>
                  {availableProviders.map((p) => <option key={p.provider} value={p.provider}>{p.providerName}</option>)}
                </select>
              </Field>
              <Field label="Model">
                <select value={model} onChange={(e) => setModel(e.target.value)} className={inputCls}>
                  <option value="">Seçin</option>
                  {selectedProvider?.models?.image?.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Marka">
                <select value={brandId} onChange={(e) => setBrandId(e.target.value)} className={inputCls}>
                  <option value="">Marka bağlamı kullanma</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Boyut">
                <div className="grid gap-2 md:grid-cols-3">
                  {IMAGE_CONTENT_SIZE_OPTIONS.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setContentSize(item.value)}
                      className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
                        contentSize === item.value
                          ? "border-blue-300 bg-blue-50 text-blue-700"
                          : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50/70 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Prompt ile Oluştur</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Prototipteki ana akış. Doğrudan prompt ile sosyal medya görseli üretir.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetPromptForm}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                  Temizle
                </button>
              </div>

              <Field label="Kalite">
                <div className="grid gap-2 md:grid-cols-3">
                  {IMAGE_QUALITY_OPTIONS.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => setQuality(item.value)}
                      className={`rounded-xl border px-3 py-3 text-left transition ${
                        quality === item.value
                          ? "border-blue-300 bg-blue-50"
                          : "border-gray-200 bg-white hover:bg-gray-50"
                      }`}
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {item.icon} {item.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{item.hint}</div>
                    </button>
                  ))}
                </div>
              </Field>

              {hasBrandLogo ? (
                <Field label="Logo Kullanımı">
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    Bu akışta marka logosu varsa her üretimde otomatik olarak AI referansı olarak gönderilir ve logo AI tarafından yerleştirilir.
                  </div>
                </Field>
              ) : null}

              <Field label="Prompt">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={5}
                  placeholder="Örn: Gençlerin ders çalışırken keyifle kahve içtiği, sıcak ışıklı, modern ve premium bir sosyal medya post görseli üret."
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <Field label="Kişi / Görsel Referansı">
                <div className="rounded-xl border border-gray-200 bg-white p-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
                      <ImagePlus className="h-3.5 w-3.5" />
                      Görsel Ekle
                      <input type="file" accept="image/*" onChange={handlePersonReferenceUpload} className="hidden" />
                    </label>
                    {personReferenceImage ? (
                      <button
                        type="button"
                        onClick={() => {
                          setPersonReferenceImage("");
                          setPersonReferenceName("");
                        }}
                        className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Kaldır
                      </button>
                    ) : null}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    Kişi, model, eğitmen veya ürün dışı bir referans yükleyin. Gemini bu görseli sahnedeki kişi/ana görsel referansı olarak kullanır.
                  </div>
                  {personReferenceImage ? (
                    <div className="mt-3 flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-2">
                      <img src={personReferenceImage} alt="Kişi referansı" className="h-16 w-16 rounded-lg border border-blue-100 object-cover" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-semibold text-gray-900">
                          {personReferenceName || "Yüklenen referans"}
                        </div>
                        <div className="mt-1 text-xs text-blue-700">
                          Prompt içinde bu kişinin yüzü, saç stili, ten tonu ve genel görünümü korunacak.
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </Field>

              {availableProducts.length ? (
                <Field label="Ürün / Hizmet Odağı">
                  <div className="space-y-3">
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-800">
                      Bir veya birden fazla ürün/hizmet seç. Varsa marka yönetimindeki ürün/hizmet görseli otomatik referans olarak gönderilir ve üretimde birebir korunur.
                    </div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {availableProducts.map((item) => {
                        const checked = selectedProductKeys.includes(item.selectionKey);
                        return (
                          <button
                            key={item.selectionKey}
                            type="button"
                            onClick={() =>
                              setSelectedProductKeys((prev) =>
                                checked
                                  ? prev.filter((key) => key !== item.selectionKey)
                                  : [...prev, item.selectionKey]
                              )
                            }
                            className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${
                              checked
                                ? "border-indigo-300 bg-indigo-50"
                                : "border-gray-200 bg-white hover:bg-gray-50"
                            }`}
                          >
                            {item.url ? (
                              <img
                                src={item.url}
                                alt={item.name}
                                className="h-11 w-11 rounded-lg border border-gray-200 object-cover"
                              />
                            ) : (
                              <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-400">
                                <Package className="w-4 h-4" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-gray-800">
                                {item.name || "Adsız ürün"}
                              </div>
                              <div className="truncate text-xs text-gray-500">
                                {item.price || item.description || "Ürün / hizmet"}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </Field>
              ) : null}

              {selectedBrand?.gallery?.length ? (
                <Field label="Mekân / Sahne Referansı">
                  <div className="space-y-3">
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                      Buradan tek bir görsel seçersen bu görsel sahnenin kendisi kabul edilir. AI yeni mekân kurmaz; seçilen mekânı koruyarak üretim yapar.
                    </div>
                    {provider !== "gemini" ? (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        Referans görselli üretim yalnız desteklenen görsel modellerinde çalışır.
                      </div>
                    ) : null}
                    <div className="grid gap-2 sm:grid-cols-2">
                      {selectedBrand.gallery.map((item, index) => {
                        const checked = selectedPromptReference === item.url;
                        return (
                          <button
                            key={`${item.url}-${index}`}
                            type="button"
                            onClick={() =>
                              setSelectedPromptReference((prev) =>
                                prev === item.url ? "" : item.url
                              )
                            }
                            className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-left transition ${
                              checked
                                ? "border-emerald-300 bg-emerald-50"
                                : "border-gray-200 bg-white hover:bg-gray-50"
                            }`}
                          >
                            {item.url ? (
                              <img
                                src={item.url}
                                alt={item.description || `Referans ${index + 1}`}
                                className="h-11 w-11 rounded-lg border border-gray-200 object-cover"
                              />
                            ) : null}
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-medium text-gray-800">
                                {item.description || `Görsel ${index + 1}`}
                              </div>
                              <div className="mt-0.5 text-xs text-gray-500">
                                {checked ? "Seçili referans" : "Referans olarak seç"}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </Field>
              ) : null}

              <div className="flex items-center justify-end gap-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                <button
                  type="button"
                  onClick={handlePromptGenerate}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 transition"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Oluştur
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Görsel Yükle & AI Düzenle</div>
                  <div className="text-xs text-gray-500 mt-1">
                    Prototipteki ikinci akış. Mevcut görseli sosyal medya kreatifine dönüştürür.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={resetEditForm}
                  className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                >
                  <RefreshCcw className="w-3.5 h-3.5" />
                  Temizle
                </button>
              </div>

              <label className="flex cursor-pointer items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-6 text-center hover:border-gray-400 hover:bg-gray-100 transition">
                <div className="space-y-2">
                  {uploadedImage ? (
                    <img
                      src={uploadedImage}
                      alt="Yüklenen görsel"
                      className="mx-auto h-28 w-auto rounded-lg border border-gray-200 object-cover"
                    />
                  ) : (
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                      <ImagePlus className="w-5 h-5" />
                    </div>
                  )}
                  <div className="text-sm font-medium text-gray-700">
                    {uploadedImageName || "Bir görsel seç"}
                  </div>
                  <div className="text-xs text-gray-500">
                    PNG veya JPG yükleyin. AI bunu temiz bir sosyal medya kreatifine dönüştürsün.
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>

              {uploadedImage && !canEditWithImage ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Yüklenen görselle düzenleme yalnız desteklenen görsel modelleriyle çalışır.
                </div>
              ) : null}

              <Field label="Düzenleme yönlendirmesi">
                <textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  rows={4}
                  placeholder="Örn: Bunu daha sıcak tonlu, premium ve reklam estetiğinde bir sosyal medya görseline dönüştür."
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                type="button"
                onClick={handleEditGenerate}
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60 transition"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                AI ile Düzenle
              </button>
            </div>
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
          </div>
        </SectionCard>

        <SectionCard>
          <h3 className="text-base font-semibold text-gray-900 mb-3">Çıktı</h3>
          {loading ? (
            <div className="flex min-h-[420px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-center">
              <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              <div className="mt-3 text-sm font-medium text-gray-800">
                AI görsel üretiyor...
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Bu işlem birkaç saniye sürebilir.
              </div>
            </div>
          ) : selectedImage?.url ? (
            <div className="space-y-4">
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
                <img
                  src={selectedImage.url}
                  alt="Üretilen görsel"
                  className="w-full object-contain"
                />
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
                <div>
                  Kaynak:{" "}
                  <span className="font-medium text-gray-800">
                    {selectedImage.sourceMode === "edit"
                      ? "Yüklenen görsel üzerinden AI düzenleme"
                      : "Prompt ile üretim"}
                  </span>
                </div>
                <div>
                  Boyut:{" "}
                  <span className="font-medium text-gray-800">
                    {selectedImage.sizeLabel}
                  </span>
                </div>
                <div>
                  Kalite:{" "}
                  <span className="font-medium text-gray-800">
                    {IMAGE_QUALITY_OPTIONS.find(
                      (item) => item.value === selectedImage.quality
                    )?.label || selectedImage.quality}
                  </span>
                </div>
                <div>
                  Sağlayıcı:{" "}
                  <span className="font-medium text-gray-800">
                    {selectedImage.provider}
                  </span>
                </div>
                <div>
                  Model:{" "}
                  <span className="font-medium text-gray-800">
                    {selectedImage.model}
                  </span>
                </div>
                <div>
                  Medya ID:{" "}
                  <span className="font-medium text-gray-800">
                    {selectedImage.mediaId}
                  </span>
                </div>
                {selectedImage.brandIdentity?.brandContextApplied ? (
                  <div>
                    Marka kimliği:{" "}
                    <span className="font-medium text-gray-800">
                      uygulandı
                    </span>
                  </div>
                ) : null}
                {selectedImage.brandIdentity?.logoApplied ? (
                  <div>
                    Logo:{" "}
                    <span className="font-medium text-gray-800">
                      gerçek logo birebir eklendi
                    </span>
                  </div>
                ) : null}
                {selectedImage.brandIdentity?.logoUsedAsReference ? (
                  <div>
                    Logo referansı:{" "}
                    <span className="font-medium text-gray-800">
                      AI üretimine gönderildi
                    </span>
                  </div>
                ) : null}
                {selectedImage.brandIdentity?.logoHandling ? (
                  <div>
                    Logo modu:{" "}
                    <span className="font-medium text-gray-800">
                      {selectedImage.brandIdentity.logoHandling === "reference"
                        ? "AI yerleştirsin"
                        : selectedImage.brandIdentity.logoHandling === "export"
                          ? "sonradan ekle"
                          : "kullanma"}
                    </span>
                  </div>
                ) : null}
                <div>
                  Prompt:{" "}
                  <span className="font-medium text-gray-800">
                    {selectedImage.prompt}
                  </span>
                </div>
                {selectedImage.referenceImage?.url ? (
                  <div>
                    Referans:{" "}
                    <span className="font-medium text-gray-800">
                      {selectedImage.referenceImage.description || "Marka galerisinden seçili görsel"}
                    </span>
                  </div>
                ) : null}
                {selectedImage.selectedProducts?.length ? (
                  <div>
                    Ürün / hizmet:{" "}
                    <span className="font-medium text-gray-800">
                      {selectedImage.selectedProducts.map((item) => item.name).join(", ")}
                    </span>
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Type className="w-4 h-4 text-gray-500" />
                  <div className="text-sm font-semibold text-gray-900">
                    Dışa Aktarma Katmanı
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <Field label="Başlık">
                    <input
                      value={overlayText}
                      onChange={(e) => setOverlayText(e.target.value)}
                      placeholder="Ana başlık"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Alt metin">
                    <input
                      value={overlaySubtext}
                      onChange={(e) => setOverlaySubtext(e.target.value)}
                      placeholder="Alt başlık veya CTA"
                      className={inputCls}
                    />
                  </Field>
                </div>
                {selectedImage.brand?.logoUrl &&
                selectedImage.brandIdentity?.logoHandling === "export" &&
                !selectedImage.brandIdentity?.logoApplied ? (
                  <label className="mt-3 inline-flex items-center gap-2 text-xs font-medium text-gray-600">
                    <input
                      type="checkbox"
                      checked={showLogo}
                      onChange={(e) => setShowLogo(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    Marka logosunu ekle
                  </label>
                ) : null}
                {selectedImage.brandIdentity?.logoHandling === "export" ? (
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    Bu üretimde AI logoyu çizmedi; üst sağ köşe logo için temiz bırakılmaya çalışıldı.
                  </div>
                ) : null}
                {selectedImage.brandIdentity?.logoUsedAsReference ? (
                  <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    Kurumsal logo üretim aşamasında AI'a referans olarak gönderildi. Gerekirse dışa aktarmada ayrıca ekleyebilirsiniz.
                  </div>
                ) : null}
                {selectedImage.brandIdentity?.logoApplied ? (
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    Bu görselde marka logosu backend tarafından zaten birebir işlendi.
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={handleExport}
                  className="mt-4 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 transition"
                >
                  <Save className="w-4 h-4" />
                  PNG İndir
                </button>
              </div>

              {generatedImages.length > 1 ? (
                <div>
                  <div className="mb-2 text-sm font-semibold text-gray-900">
                    Oluşturulan Görseller
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {generatedImages.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSelectedImage(item)}
                        className={`overflow-hidden rounded-xl border text-left transition ${
                          selectedImage.id === item.id
                            ? "border-blue-300 ring-2 ring-blue-100"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <img
                          src={item.url}
                          alt="Geçmiş üretim"
                          className="h-40 w-full object-cover"
                        />
                        <div className="p-3">
                          <div className="text-xs font-medium text-gray-800">
                            {item.sourceMode === "edit"
                              ? "AI düzenleme"
                              : "Prompt üretimi"}
                          </div>
                          <div className="mt-1 line-clamp-2 text-xs text-gray-500">
                            {item.prompt}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              <canvas ref={canvasRef} className="hidden" />
            </div>
          ) : (
            <div className="flex min-h-[360px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-400">
              Üretilen veya düzenlenen görsel burada görünecek.
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

/* ─────────────────── Video Tab ─────────────────── */
function VideoContent() {
  const { activeBrandId, brands, connectedProviders } = useSocialModule();
  const availableProviders = useMemo(
    () => buildProviderOptions(connectedProviders, "video"),
    [connectedProviders]
  );
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [brandId, setBrandId] = useState(activeBrandId || "");
  const [prompt, setPrompt] = useState("");
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [selectedProductKeys, setSelectedProductKeys] = useState([]);
  const [selectedPromptReference, setSelectedPromptReference] = useState("");
  const [presenterReferenceImage, setPresenterReferenceImage] = useState("");
  const [presenterReferenceName, setPresenterReferenceName] = useState("");
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const first = availableProviders[0];
    if (!provider && first) {
      setProvider(first.provider);
      setModel(getDefaultModelForCapability(first, "video"));
    }
  }, [availableProviders, provider]);

  useEffect(() => { setBrandId(activeBrandId || ""); }, [activeBrandId]);

  useEffect(() => {
    if (!job?.jobId || job.status !== "processing") return;
    const timer = setInterval(() => refreshStatus(job.jobId), 8000);
    return () => clearInterval(timer);
  }, [job?.jobId, job?.status]);

  const selectedProvider = useMemo(
    () => availableProviders.find((item) => item.provider === provider) || null,
    [availableProviders, provider]
  );
  const selectedBrand = useMemo(() => brands.find((b) => b.id === brandId) || null, [brandId, brands]);
  const availableProducts = useMemo(
    () =>
      (selectedBrand?.products || []).map((item, index) => ({
        ...item,
        selectionKey: `video-product-${index}`,
      })),
    [selectedBrand]
  );
  const selectedProducts = useMemo(
    () =>
      availableProducts.filter((item) => selectedProductKeys.includes(item.selectionKey)),
    [availableProducts, selectedProductKeys]
  );
  const selectedProductPayload = useMemo(
    () =>
      selectedProducts.map((item) => ({
        name: item.name,
        description: item.description || "",
        price: item.price || "",
        mediaId: item.mediaId || "",
        url: item.url || "",
      })),
    [selectedProducts]
  );
  const selectedPromptReferenceItem = useMemo(
    () =>
      selectedBrand?.gallery?.find((item) => item.url === selectedPromptReference) ||
      null,
    [selectedBrand, selectedPromptReference]
  );

  useEffect(() => {
    if (
      selectedPromptReference &&
      !selectedBrand?.gallery?.some((item) => item.url === selectedPromptReference)
    ) {
      setSelectedPromptReference("");
    }
  }, [selectedBrand, selectedPromptReference]);

  useEffect(() => {
    setSelectedProductKeys((prev) =>
      prev.filter((key) => availableProducts.some((item) => item.selectionKey === key))
    );
  }, [availableProducts]);

  const handlePresenterReferenceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      setError("");
      const dataUrl = await readFileAsDataUrl(file);
      setPresenterReferenceImage(dataUrl);
      setPresenterReferenceName(file.name || "Kişi referansı");
    } catch (err) {
      setError(err?.message || "Kişi referansı yüklenemedi.");
    }
  };

  const submit = async () => {
    if (!provider || !model || !prompt.trim()) { setError("Sağlayıcı, model ve prompt zorunlu."); return; }
    const sceneLocked = Boolean(selectedPromptReference);
    if (selectedPromptReference && provider !== "gemini") {
      setError("Referans görselli video üretimi şu anda yalnız Gemini video modellerinde çalışıyor.");
      return;
    }
    if (presenterReferenceImage && provider !== "gemini") {
      setError("Yüklenen kişi/görsel referansını kullanma şu anda yalnız Gemini video modellerinde çalışıyor.");
      return;
    }

    try {
      setLoading(true); setError("");
      const response = await ApiService.generateSocialVideo({
        provider,
        model,
        prompt: buildNativeSocialVideoPrompt({
          prompt,
          aspectRatio,
          brand: selectedBrand,
          sceneLocked,
          presenterReferenceMode: Boolean(presenterReferenceImage),
          selectedProducts,
        }),
        originalPrompt: prompt.trim(),
        brandId: brandId || null,
        aspectRatio,
        sceneLocked,
        presenterImageData: presenterReferenceImage || undefined,
        referenceImages: selectedPromptReference ? [selectedPromptReference] : [],
        selectedProducts: selectedProductPayload,
      });
      setJob({
        ...response,
        prompt: prompt.trim(),
        aspectRatio,
        sceneLocked,
        selectedProducts: selectedProductPayload,
        referenceImage: selectedPromptReferenceItem
          ? {
              url: selectedPromptReferenceItem.url,
              description: selectedPromptReferenceItem.description || "",
            }
          : null,
        presenterReference: presenterReferenceImage
          ? {
              url: presenterReferenceImage,
              description: presenterReferenceName || "Yüklenen kişi referansı",
            }
          : null,
        brand: selectedBrand
          ? {
              id: selectedBrand.id,
              name: selectedBrand.name,
              logoUrl: selectedBrand.logoUrl || null,
              colors: selectedBrand.colors || null,
              videoGuidance: selectedBrand.videoGuidance || "",
            }
          : response.brand || null,
      });
    } catch (err) {
      setError(err?.message || "Video işi başlatılamadı.");
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = async (jobId = job?.jobId) => {
    if (!jobId) return;
    try {
      setPolling(true);
      const response = await ApiService.getSocialVideoStatus(jobId);
      setJob((prev) => ({
        ...(prev || {}),
        ...response,
      }));
    } catch (err) {
      setError(err?.message || "Video durumu alınamadı.");
    } finally {
      setPolling(false);
    }
  };

  return (
    <div className="p-4 md:p-6 overflow-y-auto h-full">
      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard>
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-100 text-rose-700">
              <Clapperboard className="w-4.5 h-4.5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Video Oluştur</h2>
              <p className="text-xs text-gray-500">Video üretimi desteklenen belirli sağlayıcılarda aktiftir.</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Sağlayıcı">
                <select value={provider} onChange={(e) => { const p = availableProviders.find((x) => x.provider === e.target.value); setProvider(e.target.value); setModel(getDefaultModelForCapability(p, "video")); }} className={inputCls}>
                  <option value="">Seçin</option>
                  {availableProviders.map((p) => <option key={p.provider} value={p.provider}>{p.providerName}</option>)}
                </select>
              </Field>
              <Field label="Model">
                <select value={model} onChange={(e) => setModel(e.target.value)} className={inputCls}>
                  <option value="">Seçin</option>
                  {selectedProvider?.models?.video?.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Marka">
                <select value={brandId} onChange={(e) => setBrandId(e.target.value)} className={inputCls}>
                  <option value="">Marka bağlamı kullanma</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Video Oranı">
                <select value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)} className={inputCls}>
                  {["9:16", "16:9", "1:1"].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Prompt">
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6} placeholder="Örn: Yeni sezon kampanyası için hızlı geçişli, enerjik, 9:16 kısa reel videosu üret." className={inputCls} />
            </Field>
            <Field label="Kişi / Görsel Referansı">
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50">
                    <ImagePlus className="h-3.5 w-3.5" />
                    Görsel Ekle
                    <input type="file" accept="image/*" onChange={handlePresenterReferenceUpload} className="hidden" />
                  </label>
                  {presenterReferenceImage ? (
                    <button
                      type="button"
                      onClick={() => {
                        setPresenterReferenceImage("");
                        setPresenterReferenceName("");
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Kaldır
                    </button>
                  ) : null}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Yüklenen kişi video içinde sunucu, model veya anlatıcı referansı olarak kullanılır. En iyi sonuç için net yüzlü, iyi ışıklı görsel seçin.
                </div>
                {presenterReferenceImage ? (
                  <div className="mt-3 flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-2">
                    <img src={presenterReferenceImage} alt="Video kişi referansı" className="h-16 w-16 rounded-lg border border-rose-100 object-cover" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {presenterReferenceName || "Yüklenen referans"}
                      </div>
                      <div className="mt-1 text-xs text-rose-700">
                        Prompt içinde bu kişinin yüzü, saç stili, ten tonu ve genel görünümü korunacak.
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </Field>
            {availableProducts.length ? (
              <Field label="Ürün / Hizmet Odağı">
                <div className="space-y-2">
                  <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                    Bir veya birden fazla ürün/hizmet seç. Varsa ürün/hizmet görseli videoya otomatik referans olarak gider ve değiştirilmeden korunur.
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {availableProducts.map((item) => {
                      const checked = selectedProductKeys.includes(item.selectionKey);
                      return (
                        <button
                          key={item.selectionKey}
                          type="button"
                          onClick={() =>
                            setSelectedProductKeys((prev) =>
                              checked
                                ? prev.filter((key) => key !== item.selectionKey)
                                : [...prev, item.selectionKey]
                            )
                          }
                          className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                            checked
                              ? "border-rose-300 bg-rose-50"
                              : "border-gray-200 bg-white hover:bg-gray-50"
                          }`}
                        >
                          {item.url ? (
                            <img
                              src={item.url}
                              alt={item.name}
                              className="h-10 w-10 rounded-lg border border-gray-200 object-cover"
                            />
                          ) : (
                            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${checked ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-500"}`}>
                              <Package className="w-4 h-4" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-gray-800">
                              {item.name || "Adsız ürün"}
                            </div>
                            <div className="mt-0.5 truncate text-xs text-gray-500">
                              {item.price || item.description || "Ürün / hizmet"}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Field>
            ) : null}
            {selectedBrand?.gallery?.length ? (
              <Field label="Mekân / Sahne Referansı">
                <div className="space-y-2">
                  <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    Burada seçilen görsel videodaki gerçek mekân kabul edilir. Sistem sahneyi bozmadan kullanması için AI'a kilitli sahne kuralı gönderir.
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {selectedBrand.gallery.map((item, index) => {
                      const checked = selectedPromptReference === item.url;
                      return (
                        <button
                          key={`${item.url}-${index}`}
                          type="button"
                          onClick={() =>
                            setSelectedPromptReference((prev) =>
                              prev === item.url ? "" : item.url
                            )
                          }
                          className={`flex items-center gap-3 rounded-xl border px-3 py-3 text-left transition ${
                            checked
                              ? "border-blue-300 bg-blue-50"
                              : "border-gray-200 bg-white hover:bg-gray-50"
                          }`}
                        >
                          {item.url ? (
                            <img
                              src={item.url}
                              alt={item.description || `Referans ${index + 1}`}
                              className="h-11 w-11 rounded-lg border border-gray-200 object-cover"
                            />
                          ) : null}
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-gray-800">
                              {item.description || `Görsel ${index + 1}`}
                            </div>
                            <div className="mt-0.5 text-xs text-gray-500">
                              {checked ? "Seçili sahne referansı" : "Sahne referansı olarak seç"}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Field>
            ) : null}
            {!availableProviders.length && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Video üretimi için önce desteklenen sağlayıcı anahtarınızı API Ayarları sekmesinde bağlamalısınız.
              </div>
            )}
            {selectedProvider && !selectedProvider.capabilities?.video && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {selectedProvider.providerName} video üretimini desteklemiyor.
              </div>
            )}
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
            <div className="flex items-center justify-end gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
              <button onClick={submit} disabled={loading || !selectedProvider || !selectedProvider.capabilities?.video} className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60 transition">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Video Üretimini Başlat
              </button>
            </div>
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">İş Durumu</h3>
            <button onClick={() => refreshStatus()} disabled={!job?.jobId || polling} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition">
              {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
              Durumu Yenile
            </button>
          </div>
          {!job ? (
            <div className="flex min-h-[360px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-xs text-gray-400">
              Başlatılan video işi burada takip edilir.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
                <div>Sağlayıcı: <span className="font-medium text-gray-800">{job.provider}</span></div>
                <div>Model: <span className="font-medium text-gray-800">{job.model}</span></div>
                <div>Durum: <span className="font-medium text-gray-800">{job.status}</span></div>
                {job.fileState ? (
                  <div>Dosya durumu: <span className="font-medium text-gray-800">{job.fileState}</span></div>
                ) : null}
                {job.error && <div className="text-red-600 mt-1">Hata: {job.error}</div>}
              </div>
              {(job.brandIdentity?.brandContextApplied || job.referenceImage?.url || job.presenterReference?.url || job.selectedProducts?.length || job.aspectRatio) && (
                <div className="rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600 space-y-1">
                  {job.brandIdentity?.brandContextApplied ? (
                    <div>Marka kimliği: <span className="font-medium text-gray-800">uygulandı</span></div>
                  ) : null}
                  {job.aspectRatio ? (
                    <div>Oran: <span className="font-medium text-gray-800">{job.aspectRatio}</span></div>
                  ) : null}
                  {job.referenceImage?.url ? (
                    <div>
                      Referans sahne: <span className="font-medium text-gray-800">{job.referenceImage.description || "Marka galerisinden seçili görsel"}</span>
                    </div>
                  ) : null}
                  {job.presenterReference?.url ? (
                    <div>
                      Kişi referansı: <span className="font-medium text-gray-800">{job.presenterReference.description || "Yüklenen kişi görseli"}</span>
                    </div>
                  ) : null}
                  {job.selectedProducts?.length ? (
                    <div>
                      Ürün / hizmet: <span className="font-medium text-gray-800">{job.selectedProducts.map((item) => item.name).join(", ")}</span>
                    </div>
                  ) : null}
                </div>
              )}
              {job.url ? (
                <video src={job.url} controls className="w-full rounded-xl border border-gray-200" />
              ) : (
                <div className={`flex min-h-[240px] items-center justify-center rounded-xl border border-dashed bg-gray-50 px-4 text-center text-xs ${job.error ? "border-red-200 text-red-600" : "border-gray-300 text-gray-400"}`}>
                  {job.error || (job.status === "processing" ? "Video hazırlanıyor. Otomatik yenileme açık." : "Henüz video dosyası hazır değil.")}
                </div>
              )}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

/* ─────────────────── Generated Content Tab ─────────────────── */
function GeneratedContent() {
  const [typeFilter, setTypeFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  const visibleItems = useMemo(
    () =>
      typeFilter === "all"
        ? items
        : items.filter((item) => item.type === typeFilter),
    [items, typeFilter]
  );

  const selectedItem = useMemo(
    () => visibleItems.find((item) => item.id === selectedId) || visibleItems[0] || null,
    [visibleItems, selectedId]
  );

  const counts = useMemo(
    () => ({
      all: items.length,
      image: items.filter((item) => item.type === "image").length,
      video: items.filter((item) => item.type === "video").length,
    }),
    [items]
  );

  const loadItems = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await ApiService.getSocialGeneratedContents({
        limit: 120,
      });
      const nextItems = Array.isArray(data) ? data : [];
      setItems(nextItems);
      setSelectedId((prev) =>
        nextItems.some((item) => item.id === prev) ? prev : nextItems[0]?.id || ""
      );
    } catch (err) {
      setError(err?.message || "Oluşturulan içerikler alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const deleteItem = async (item) => {
    if (!item?.id) return;
    if (!window.confirm("Bu içeriği silmek istiyor musunuz?")) return;

    try {
      setBusyId(item.id);
      setError("");
      await ApiService.deleteSocialGeneratedContent(item.id);
      setItems((prev) => prev.filter((content) => content.id !== item.id));
      setSelectedId((prev) => (prev === item.id ? "" : prev));
    } catch (err) {
      setError(err?.message || "İçerik silinemedi.");
    } finally {
      setBusyId("");
    }
  };

  const refreshVideoStatus = async (item) => {
    if (!item?.jobId) return;

    try {
      setBusyId(item.id);
      setError("");
      await ApiService.getSocialVideoStatus(item.jobId);
      await loadItems();
    } catch (err) {
      setError(err?.message || "Video durumu güncellenemedi.");
    } finally {
      setBusyId("");
    }
  };

  const filterOptions = [
    { value: "all", label: "Tümü", count: counts.all },
    { value: "image", label: "Görseller", count: counts.image },
    { value: "video", label: "Videolar", count: counts.video },
  ];

  return (
    <div className="p-4 md:p-6 overflow-y-auto h-full">
      <div className="grid gap-4 xl:grid-cols-[0.92fr_1.08fr]">
        <SectionCard>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="text-base font-semibold text-gray-900">Oluşturulan İçerikler</h2>
              <p className="mt-1 text-xs text-gray-500">
                Görsel ve video üretimleri otomatik burada saklanır.
              </p>
            </div>
            <button
              type="button"
              onClick={loadItems}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5" />}
              Yenile
            </button>
          </div>

          <div className="mb-4 grid grid-cols-3 gap-2">
            {filterOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setTypeFilter(option.value)}
                className={`rounded-xl border px-3 py-2 text-left transition ${
                  typeFilter === option.value
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                }`}
              >
                <div className="text-xs font-semibold">{option.label}</div>
                <div className="mt-1 text-lg font-bold">{option.count}</div>
              </button>
            ))}
          </div>

          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          ) : null}

          {loading ? (
            <div className="flex min-h-[360px] items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              İçerikler yükleniyor...
            </div>
          ) : visibleItems.length ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {visibleItems.map((item) => {
                const selected = selectedItem?.id === item.id;
                const isVideo = item.type === "video";
                const isProcessing = item.status === "processing";

                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={`overflow-hidden rounded-2xl border text-left transition ${
                      selected
                        ? "border-blue-300 bg-blue-50 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <div className="aspect-[4/3] bg-gray-100">
                      {item.url && !isVideo ? (
                        <img src={item.url} alt={item.title || "Oluşturulan görsel"} className="h-full w-full object-cover" />
                      ) : item.url && isVideo ? (
                        <video src={item.url} className="h-full w-full object-cover" muted />
                      ) : (
                        <div className="flex h-full flex-col items-center justify-center gap-2 text-gray-400">
                          {isVideo ? <Video className="h-8 w-8" /> : <ImageIcon className="h-8 w-8" />}
                          <span className="text-xs">{isProcessing ? "Video hazırlanıyor" : "Önizleme yok"}</span>
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${
                          isVideo ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"
                        }`}>
                          {isVideo ? <Video className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                          {isVideo ? "Video" : "Görsel"}
                        </span>
                        <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                          item.status === "completed"
                            ? "bg-emerald-100 text-emerald-700"
                            : item.status === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-slate-100 text-slate-600"
                        }`}>
                          {item.status === "completed" ? "Hazır" : item.status === "failed" ? "Hata" : "İşleniyor"}
                        </span>
                      </div>
                      <div className="mt-2 line-clamp-2 text-sm font-semibold text-gray-900">
                        {item.title || "Sosyal medya içeriği"}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">{formatSocialDate(item.createdAt)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex min-h-[360px] items-center justify-center rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 text-center text-sm text-gray-500">
              Henüz kayıtlı içerik yok. Görsel veya video üretince otomatik burada görünecek.
            </div>
          )}
        </SectionCard>

        <SectionCard>
          {selectedItem ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-gray-400">
                    {selectedItem.type === "video" ? <Video className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
                    {selectedItem.type === "video" ? "Video İçeriği" : "Görsel İçeriği"}
                  </div>
                  <h3 className="mt-2 text-lg font-semibold text-gray-900">
                    {selectedItem.title || "Sosyal medya içeriği"}
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">{formatSocialDate(selectedItem.createdAt)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedItem.status === "processing" && selectedItem.jobId ? (
                    <button
                      type="button"
                      onClick={() => refreshVideoStatus(selectedItem)}
                      disabled={busyId === selectedItem.id}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    >
                      {busyId === selectedItem.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
                      Durumu Yenile
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => downloadSocialAsset(selectedItem)}
                    disabled={!selectedItem.url}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                  >
                    <Download className="h-3.5 w-3.5" />
                    İndir
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteItem(selectedItem)}
                    disabled={busyId === selectedItem.id}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-60"
                  >
                    {busyId === selectedItem.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    Sil
                  </button>
                </div>
              </div>

              <div className="overflow-hidden rounded-3xl border border-gray-200 bg-gray-950">
                {selectedItem.url && selectedItem.type === "image" ? (
                  <img src={selectedItem.url} alt={selectedItem.title || "Oluşturulan görsel"} className="max-h-[620px] w-full object-contain" />
                ) : selectedItem.url && selectedItem.type === "video" ? (
                  <video src={selectedItem.url} className="max-h-[620px] w-full bg-black" controls />
                ) : (
                  <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 text-gray-300">
                    {selectedItem.type === "video" ? <Video className="h-12 w-12" /> : <ImageIcon className="h-12 w-12" />}
                    <div className="text-sm">
                      {selectedItem.status === "processing" ? "İçerik hazırlanıyor." : "Önizleme bulunamadı."}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Model</div>
                  <div className="mt-1 truncate text-sm font-semibold text-gray-900">{selectedItem.model || "-"}</div>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Sağlayıcı</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">{selectedItem.provider || "-"}</div>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs text-gray-500">Oran / Kalite</div>
                  <div className="mt-1 text-sm font-semibold text-gray-900">{selectedItem.aspectRatio || selectedItem.metadata?.quality || "-"}</div>
                </div>
              </div>

              {selectedItem.brand?.name ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="text-xs font-semibold text-gray-500">Marka</div>
                  <div className="mt-2 flex items-center gap-3">
                    {selectedItem.brand.logoUrl ? (
                      <img src={selectedItem.brand.logoUrl} alt={selectedItem.brand.name} className="h-10 w-10 rounded-xl border border-gray-200 object-contain" />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-500">
                        <Building2 className="h-4 w-4" />
                      </div>
                    )}
                    <div className="text-sm font-semibold text-gray-900">{selectedItem.brand.name}</div>
                  </div>
                </div>
              ) : null}

              {selectedItem.prompt ? (
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-gray-500">
                    <Eye className="h-3.5 w-3.5" />
                    Prompt
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">{selectedItem.prompt}</p>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="flex min-h-[620px] items-center justify-center rounded-3xl border border-dashed border-gray-200 bg-gray-50 text-sm text-gray-500">
              Seçili içerik yok.
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

/* ─────────────────── Post Tab ─────────────────── */
function PostContent() {
  const { activeBrandId, brands, connectedProviders } = useSocialModule();
  const availableProviders = useMemo(() => buildProviderOptions(connectedProviders, "text"), [connectedProviders]);
  const [provider, setProvider] = useState("");
  const [model, setModel] = useState("");
  const [brandId, setBrandId] = useState(activeBrandId || "");
  const [platform, setPlatform] = useState("Instagram");
  const [tone, setTone] = useState("Samimi ve güven veren");
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const first = availableProviders[0];
    if (!provider && first) { setProvider(first.provider); setModel(getDefaultModelForCapability(first, "text")); }
  }, [availableProviders, provider]);

  useEffect(() => { setBrandId(activeBrandId || ""); }, [activeBrandId]);

  const selectedProvider = useMemo(() => availableProviders.find((p) => p.provider === provider) || null, [availableProviders, provider]);

  const submit = async () => {
    if (!provider || !model || !prompt.trim()) { setError("Sağlayıcı, model ve brief zorunlu."); return; }
    try {
      setLoading(true); setError("");
      const response = await ApiService.generateSocialText({
        provider, model,
        prompt: `Platform: ${platform}\nTon: ${tone}\nİstek: ${prompt}`,
        brandId: brandId || null, platform, tone,
      });
      setResult(response.content || "");
    } catch (err) {
      setError(err?.message || "Post üretilemedi.");
    } finally {
      setLoading(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    try { await navigator.clipboard.writeText(result); } catch (_) {}
  };

  return (
    <div className="p-4 md:p-6 overflow-y-auto h-full">
      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard>
          <div className="flex items-center gap-3 mb-4">
            <div className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <PenSquare className="w-4.5 h-4.5" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Post Editörü</h2>
              <p className="text-xs text-gray-500">Marka bağlamı ile platforma uygun ilk taslağı üretin.</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Field label="Sağlayıcı">
                <select value={provider} onChange={(e) => { const p = availableProviders.find((x) => x.provider === e.target.value); setProvider(e.target.value); setModel(getDefaultModelForCapability(p, "text")); }} className={inputCls}>
                  <option value="">Seçin</option>
                  {availableProviders.map((p) => <option key={p.provider} value={p.provider}>{p.providerName}</option>)}
                </select>
              </Field>
              <Field label="Model">
                <select value={model} onChange={(e) => setModel(e.target.value)} className={inputCls}>
                  <option value="">Seçin</option>
                  {selectedProvider?.models?.text?.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <Field label="Marka">
                <select value={brandId} onChange={(e) => setBrandId(e.target.value)} className={inputCls}>
                  <option value="">Bağlam kullanma</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Platform">
                <input type="text" value={platform} onChange={(e) => setPlatform(e.target.value)} className={inputCls} />
              </Field>
              <Field label="Ton">
                <input type="text" value={tone} onChange={(e) => setTone(e.target.value)} className={inputCls} />
              </Field>
            </div>
            <Field label="Brief">
              <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={7} placeholder="Örn: Yeni ürün lansmanı için merak uyandıran, ilk yorum davetli, kısa ve güçlü bir Instagram postu yaz." className={inputCls} />
            </Field>
            {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
            <button onClick={submit} disabled={loading} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60 transition">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Taslak Üret
            </button>
          </div>
        </SectionCard>

        <SectionCard>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">Editör</h3>
            <button onClick={copyResult} disabled={!result} className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition">
              <Copy className="w-3.5 h-3.5" />
              Kopyala
            </button>
          </div>
          <textarea
            value={result}
            onChange={(e) => setResult(e.target.value)}
            rows={20}
            placeholder="Üretilen post taslağı burada görünecek."
            className={`${inputCls} resize-none`}
          />
        </SectionCard>
      </div>
    </div>
  );
}


/* Social Module Shell */
function SocialModuleShell() {
  const { loading, error } = useSocialModule();

  const canManageProvider = checkPermission("social_provider_manage");
  const canManageBrand = checkPermission("social_brand_manage");
  const canGenerateImage = checkPermission("social_generate_image");
  const canGenerateVideo = checkPermission("social_generate_video");
  const canGenerateText = checkPermission("social_generate_text");

  const tabs = useMemo(() => {
    const list = [
      {
        type: "custom",
        title: "Ana Sayfa",
        path: "dashboard",
        customComponent: <DashboardContent />,
      },
    ];

    if (canManageProvider) {
      list.push({
        type: "custom",
        title: "API Ayarları",
        path: "settings",
        customComponent: <SettingsContent />,
      });
    }

    if (canManageBrand) {
      list.push({
        type: "custom",
        title: "Marka Yönetimi",
        path: "brand",
        customComponent: <BrandContent />,
      });
    }

    if (canGenerateImage) {
      list.push({
        type: "custom",
        title: "Görsel Oluştur",
        path: "image",
        customComponent: <ImageContent />,
      });
    }

    if (canGenerateVideo) {
      list.push({
        type: "custom",
        title: "Video Oluştur",
        path: "video",
        customComponent: <VideoContent />,
      });
    }

    list.push({
      type: "custom",
      title: "İçerikler",
      path: "contents",
      customComponent: <GeneratedContent />,
    });

    if (canGenerateText) {
      list.push({
        type: "custom",
        title: "Post Editörü",
        path: "post",
        customComponent: <PostContent />,
      });
    }

    return list;
  }, [
    canGenerateImage,
    canGenerateText,
    canGenerateVideo,
    canManageBrand,
    canManageProvider,
  ]);

  if (loading) {
    return (
      <div className="h-[calc(100dvh-4rem)] bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600 text-sm">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[calc(100dvh-4rem)] bg-slate-50 overflow-hidden flex">
        <div className="flex-1 min-w-0 flex items-center justify-center p-6">
          <div className="text-center text-red-600 font-medium bg-red-50 p-4 rounded-xl border border-red-200">{error}</div>
        </div>
      </div>
    );
  }

  return <TabPage title="Sosyal Medya" tabs={tabs} />;
}

export default function SocialModuleLayout() {
  if (!checkPermission("social_list")) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <SocialModuleProvider>
      <SocialModuleShell />
    </SocialModuleProvider>
  );
}
